import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { RiFolderUploadLine } from "react-icons/ri";
import { FaArrowUpLong } from "react-icons/fa6";
import { BsFillSendFill } from "react-icons/bs";
import { FiPaperclip, FiXCircle, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { SiChatbot } from "react-icons/si";
import { FaFileAlt, FaMicrosoft, FaGoogleDrive, FaFolder } from "react-icons/fa";
import { HiOutlineDocumentAdd } from "react-icons/hi";
import { RiShareBoxLine } from "react-icons/ri";
import googleDriveIcon from "../../assets/googleicon.svg";
import oneDriveIcon from "../../assets/oneDriveIcon.svg";
import sharePointIcon from "../../assets/ms-sharepoint-svgrepo-com.svg";

import moment from "moment/dist/moment.js";
import MarkdownMessage from "../../components/MarkdownMessage";

import UploadModal from "../../components/UploadModal";
import Loader from "../../components/Loader";
import ENDPOINTS from "../../api/endpoint";
import { showErrorToast, showSuccessToast } from "../../components/Toast";
import { GOOGLE_DRIVE_CONFIG } from "../../config/google-config";
import { ONEDRIVE_CONFIG } from "../../config/onedrive-config";
import { SHAREPOINT_CONFIG } from "../../config/sharepoint-config";
import OneDriveFilePicker from "../../components/OneDriveFilePicker";
import GoogleDrivePicker from "../../components/GoogleDrivePicker";
import DriveFolderPicker from "../../components/DriveFolderPicker";

export default function ChatbotMain() {
  const { chatbotId } = useParams();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [conversationId, setConversationId] = useState(null);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [isUploadDropdownOpen, setIsUploadDropdownOpen] = useState(false);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [pickerApiLoaded, setPickerApiLoaded] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [isOneDriveSignedIn, setIsOneDriveSignedIn] = useState(false);
  const [oneDriveAccessToken, setOneDriveAccessToken] = useState(null);
  const [oneDriveReady, setOneDriveReady] = useState(false);
  const [isOneDrivePickerOpen, setIsOneDrivePickerOpen] = useState(false);
  const [isOneDriveFolderMode, setIsOneDriveFolderMode] = useState(false);
  const [isSharePointSignedIn, setIsSharePointSignedIn] = useState(false);
  const [sharePointAccessToken, setSharePointAccessToken] = useState(null);
  const [isSharePointPickerOpen, setIsSharePointPickerOpen] = useState(false);
  // Company-scoped drive connection records (independent of this browser's token
  // validity). These drive the Disconnect button so an admin can always clear a
  // stale/expired company connection — including the case where the frontend
  // shows NOT connected but the backend still holds an orphaned record.
  const [googleDriveCompanyConnected, setGoogleDriveCompanyConnected] =
    useState(false);
  const [oneDriveCompanyConnected, setOneDriveCompanyConnected] =
    useState(false);
  const [sharePointCompanyConnected, setSharePointCompanyConnected] =
    useState(false);
  const [isDisconnectingDrive, setIsDisconnectingDrive] = useState(false);
  const [isUploadingGoogleDrive, setIsUploadingGoogleDrive] = useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [googleDrivePickerOpen, setGoogleDrivePickerOpen] = useState(false);
  const [googleDrivePickerData, setGoogleDrivePickerData] = useState(null);
  const [drivePickerMode, setDrivePickerMode] = useState("files"); // "files" | "folders"
  const [drivePickerDriveType, setDrivePickerDriveType] = useState("google_drive"); // "google_drive" | "onedrive" | "sharepoint"
  const [drivePickerTitle, setDrivePickerTitle] = useState("Select from Google Drive");
  const [drivePickerConfirmLabel, setDrivePickerConfirmLabel] = useState(undefined);
  const [driveFolderPickerOpen, setDriveFolderPickerOpen] = useState(false);
  const [driveFolderPickerType, setDriveFolderPickerType] = useState("google_drive"); // "google_drive" | "onedrive" | "sharepoint"
  // Folders this chatbot auto-syncs from (DriveFolderMonitor rows scoped to this
  // chatbot). The company drive account is shared; each chatbot points at its own
  // folder(s). folderActionId marks the row currently being removed/synced.
  const [chatbotFolders, setChatbotFolders] = useState([]);
  const [chatbotFoldersLoading, setChatbotFoldersLoading] = useState(false);
  const [folderActionId, setFolderActionId] = useState(null);
  const [chatbotName, setChatbotName] = useState("");
  const [automatedFolders, setAutomatedFolders] = useState([]);
  const [automatedOneDriveFolders, setAutomatedOneDriveFolders] = useState([]);
  const [isFolderPickerMode, setIsFolderPickerMode] = useState(false);
  const closeUploadModal = () => setIsUploadModalOpen(false);

  const messagesEndRef = useRef(null);
  const messagesRef = useRef({});
  const scrollToFirstRef = useRef(false);
  const location = useLocation();
  const welcomeDropdownRef = useRef(null);
  const chatDropdownRef = useRef(null);
  const navigate = useNavigate();

  // Refs to store latest ingestion functions to avoid re-creating intervals
  const ingestGoogleDriveRef = useRef(null);
  const ingestOneDriveRef = useRef(null);
  const isIngestingGoogleDriveRef = useRef(false);
  const isIngestingOneDriveRef = useRef(false);

  const normalizeDocumentSource = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string") {
      try {
        const parsed = JSON.parse(v);
        return Array.isArray(parsed) ? parsed : [v];
      } catch {
        return [v];
      }
    }
    return v ? [v] : [];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        welcomeDropdownRef.current &&
        !welcomeDropdownRef.current.contains(event.target) &&
        chatDropdownRef.current &&
        !chatDropdownRef.current.contains(event.target)
      ) {
        setIsUploadDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadConversationData = useCallback(
    async (conversationId, scrollToFirst = false) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;

      if (!companyId || !token || !userId || !conversationId) {
        showErrorToast("Missing authentication details or conversation ID ❌");
        return;
      }

      setIsLoadingConversation(true);
      scrollToFirstRef.current = scrollToFirst; // Set flag for scrolling

      try {
        const res = await fetch(
          ENDPOINTS.GET_SPECIFIC_CONVERSATION(
            companyId,
            conversationId,
            userId
          ),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();

        if (!res.ok) {
          showErrorToast(data.detail || "Failed to load conversation");
          scrollToFirstRef.current = false; // Reset on error
          return;
        }

        const conversationMessages = [];

        if (data.qa_pairs && data.qa_pairs.length > 0) {
          data.qa_pairs.forEach((qaPair) => {
            conversationMessages.push({
              id: `u-${qaPair.question_id}`,
              text: qaPair.question,
              timestamp: formatTimestamp(qaPair.question_created_at),
              rawTimestamp: qaPair.question_created_at,
              isUser: true,
            });

            conversationMessages.push({
              id: `a-${qaPair.answer_id}`,
              text: qaPair.answer,
              timestamp: formatTimestamp(qaPair.answer_created_at),
              rawTimestamp: qaPair.answer_created_at,
              documentSource: normalizeDocumentSource(
                qaPair.document_source ?? qaPair.document_sources
              ),
              isUser: false,
            });
          });
        }

        setMessages(conversationMessages);
        setConversationId(conversationId);
        setHasStartedConversation(true);
        localStorage.setItem("conversation_id", conversationId);

        if (window.updateSidebarActiveConversation) {
          window.updateSidebarActiveConversation(conversationId);
        }
      } catch (err) {
        console.error("Error loading conversation:", err);
        showErrorToast("Failed to load conversation");
        scrollToFirstRef.current = false; // Reset on error
      } finally {
        setIsLoadingConversation(false);
      }
    },
    []
  );

  React.useEffect(() => {
    console.log("Home rerender triggered:", location.state?.refresh);

    // If navigating from search with a conversation ID, load it
    if (location.state?.conversationId && location.state?.fromSearch) {
      const conversationId = location.state.conversationId;
      // Small delay to ensure component is mounted
      setTimeout(() => {
        loadConversationData(conversationId, true); // true = scroll to first message
      }, 200);
    }
  }, [location.state, loadConversationData]);

  // Clear messages and conversation when chatbotId changes or when navigating to chatbot without conversation
  useEffect(() => {
    if (chatbotId) {
      // Check if there's a conversation_id in localStorage
      const savedConversationId = localStorage.getItem("conversation_id");

      // If no conversation is active, clear messages to show welcome container
      // This ensures welcome container shows when clicking chatbot (not conversation)
      if (!savedConversationId) {
        // Always clear messages when there's no conversation_id
        // This handles the case when clicking on chatbot (not conversation)
        setMessages([]);
        setConversationId(null);
        setHasStartedConversation(false);
      }
    }
  }, [chatbotId, location.pathname]);

  useEffect(() => {
    window.loadConversationData = loadConversationData;
    return () => {
      delete window.loadConversationData;
    };
  }, [loadConversationData]);

  const fetchGoogleDriveTokenFromBackend = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) return null;
    try {
      const response = await fetch(ENDPOINTS.SAVE_GOOGLE_DRIVE_TOKEN(companyId), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      if (!response.ok) {
        if (response.status !== 404) {
          const errorData = await response.json();
          console.error("Failed to fetch Google Drive token from backend:", errorData);
        }
        return null;
      }
      const data = await response.json();
      if (data.token && data.expires_at) {
        localStorage.setItem("google_drive_access_token", data.token);
        localStorage.setItem("google_drive_token_expiry", data.expires_at.toString());
        return { token: data.token, expires_at: data.expires_at };
      }
      return null;
    } catch (error) {
      console.error("Error fetching Google Drive token from backend:", error);
      return null;
    }
  }, []);

  const fetchOneDriveTokenFromBackend = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) return null;
    try {
      const response = await fetch(ENDPOINTS.SAVE_ONEDRIVE_TOKEN(companyId), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      if (!response.ok) {
        if (response.status !== 404) {
          const err = await response.json();
          console.error("Failed to fetch OneDrive token from backend:", err);
        }
        return null;
      }
      const data = await response.json();
      if (data.token && data.expires_at) {
        return {
          token: data.token,
          expires_at: data.expires_at,
          refresh_token: data.refresh_token || null,
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching OneDrive token from backend:", error);
      return null;
    }
  }, []);

  const fetchGoogleDriveDocumentsFromBackend = useCallback(
    async (folderId = null) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;
      if (!companyId || !userId) return null;
      try {
        const url = ENDPOINTS.GET_GOOGLE_DRIVE_DOCUMENTS(
          companyId,
          500,
          folderId || undefined
        );
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
          },
        });
        if (!response.ok) {
          const err = await response.json();
          showErrorToast(err.detail || err.message || "Failed to list Google Drive documents");
          return null;
        }
        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching Google Drive documents:", error);
        showErrorToast("Failed to load Google Drive documents");
        return null;
      }
    },
    []
  );

  // Load the folders this chatbot is set to auto-sync from (admin only).
  const fetchChatbotFolders = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId || !chatbotId || user?.role !== "admin") return;
    setChatbotFoldersLoading(true);
    try {
      const res = await fetch(
        `${ENDPOINTS.INGESTION_FOLDERS(companyId)}?chatbot_id=${chatbotId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", "X-User-Id": userId },
        }
      );
      if (!res.ok) {
        setChatbotFolders([]);
        return;
      }
      const data = await res.json();
      setChatbotFolders(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error loading chatbot folders:", e);
      setChatbotFolders([]);
    } finally {
      setChatbotFoldersLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    fetchChatbotFolders();
  }, [fetchChatbotFolders]);

  const removeChatbotFolder = useCallback(
    async (monitorId, folderName) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;
      if (!companyId || !userId) {
        showErrorToast("Missing authentication details");
        return;
      }
      if (
        !window.confirm(
          `Stop syncing "${folderName || "this folder"}" for this chatbot? Files already imported are not deleted.`
        )
      ) {
        return;
      }
      setFolderActionId(monitorId);
      try {
        const res = await fetch(
          `${ENDPOINTS.INGESTION_FOLDERS(companyId)}/${monitorId}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json", "X-User-Id": userId },
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showErrorToast(data.detail || data.message || "Failed to remove folder");
          return;
        }
        showSuccessToast("Folder removed.");
        fetchChatbotFolders();
      } catch (e) {
        console.error("Error removing folder:", e);
        showErrorToast("Failed to remove folder");
      } finally {
        setFolderActionId(null);
      }
    },
    [fetchChatbotFolders]
  );

  const runFolderNow = useCallback(
    async (monitorId) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;
      if (!companyId || !userId) return;
      setFolderActionId(monitorId);
      try {
        const res = await fetch(
          ENDPOINTS.INGESTION_FOLDER_RUN_NOW(companyId, monitorId),
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-User-Id": userId },
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          showErrorToast(data.detail || data.message || "Failed to start sync");
          return;
        }
        showSuccessToast("Sync started. New files will appear shortly.");
        fetchChatbotFolders();
      } catch (e) {
        console.error("Error running folder sync:", e);
        showErrorToast("Failed to start sync");
      } finally {
        setFolderActionId(null);
      }
    },
    [fetchChatbotFolders]
  );

  const createIngestionFolderMonitor = useCallback(
    async ({ driveType, folderId, folderName, driveId, intervalHours = 5 }) => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;
      if (!companyId || !userId) {
        showErrorToast("Missing authentication details");
        return false;
      }
      try {
        const response = await fetch(ENDPOINTS.INGESTION_FOLDERS(companyId), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
          },
          body: JSON.stringify({
            drive_type: driveType,
            folder_id: folderId,
            drive_id: driveId || undefined,
            folder_name: folderName,
            interval_hours: intervalHours,
            chatbot_id: chatbotId || null,
          }),
        });
        const data = await response.json();
        if (!response.ok) {
          showErrorToast(
            data.detail || data.message || "Failed to enable auto ingestion"
          );
          return false;
        }
        showSuccessToast("Folder added. This chatbot will auto-sync from it.");
        fetchChatbotFolders();
        return true;
      } catch (error) {
        console.error("Error enabling ingestion folder:", error);
        showErrorToast("Failed to enable auto ingestion");
        return false;
      }
    },
    [chatbotId, fetchChatbotFolders]
  );

  const openGoogleDrivePickerFromBackend = useCallback(async () => {
    setIsUploadingGoogleDrive(true);
    try {
      const data = await fetchGoogleDriveDocumentsFromBackend();
      if (data != null) {
        setGoogleDrivePickerData(data);
        setDrivePickerMode("files");
        setDrivePickerDriveType("google_drive");
        setDrivePickerTitle("Select from Google Drive");
        setDrivePickerConfirmLabel(undefined);
        setGoogleDrivePickerOpen(true);
      }
    } finally {
      setIsUploadingGoogleDrive(false);
    }
  }, [fetchGoogleDriveDocumentsFromBackend]);

  const openGoogleDriveFolderPickerForIngestion = useCallback(() => {
    setIsUploadDropdownOpen(false);
    setDriveFolderPickerType("google_drive");
    setDriveFolderPickerOpen(true);
  }, []);

  const openSharePointFolderPickerForIngestion = useCallback(() => {
    setIsUploadDropdownOpen(false);
    setDriveFolderPickerType("sharepoint");
    setDriveFolderPickerOpen(true);
  }, []);

  const openOneDriveFolderPickerForIngestion = useCallback(() => {
    setIsUploadDropdownOpen(false);
    setDriveFolderPickerType("onedrive");
    setDriveFolderPickerOpen(true);
  }, []);

  // Function to save Google Drive token to backend
  // USED IN: handleGoogleDriveCallback ~line 666, openGoogleDrivePickerWithToken ~line 1935, handleGoogleDriveConnect ~line 3021
  // const saveGoogleDriveTokenToBackend = useCallback(async (token, expiresAt) => { ... }, []);
  const saveGoogleDriveTokenToBackend = useCallback(async () => { }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user?.role === "admin") setIsAdmin(true);
    setUserInfo(user);

    // Fetch Google Drive token from backend first
    const loadGoogleDriveToken = async () => {
      const backendTokenData = await fetchGoogleDriveTokenFromBackend();

      if (backendTokenData) {
        const now = new Date().getTime();
        const expiresAt = parseInt(backendTokenData.expires_at);

        // Check if token from backend is still valid
        if (now < expiresAt) {
          console.log(
            "Valid Google Drive token found in backend, restoring session"
          );
          localStorage.setItem(
            "google_drive_access_token",
            backendTokenData.token
          );
          localStorage.setItem(
            "google_drive_token_expiry",
            expiresAt.toString()
          );
          setAccessToken(backendTokenData.token);
          setIsSignedIn(true);
          localStorage.removeItem("google_drive_auth_redirect");
          return;
        } else {
          console.log("Google Drive token from backend is expired");
        }
      }

      // Fallback to localStorage if backend doesn't have a valid token
      const storedToken = localStorage.getItem("google_drive_access_token");
      const tokenExpiry = localStorage.getItem("google_drive_token_expiry");

      console.log("Checking stored token:", {
        storedToken: !!storedToken,
        tokenExpiry,
      });

      if (storedToken && tokenExpiry) {
        const now = new Date().getTime();
        const expiry = parseInt(tokenExpiry);

        console.log("Token expiry check:", {
          now,
          expiry,
          isValid: now < expiry,
        });

        if (now < expiry) {
          // Token is still valid
          console.log("Token is valid, restoring session");
          setAccessToken(storedToken);
          setIsSignedIn(true);
        } else {
          // Token expired, try to refresh if refresh token exists
          const refreshToken = localStorage.getItem(
            "google_drive_refresh_token"
          );
          if (refreshToken) {
            console.log(
              "Token expired, but refresh token exists - will refresh on next use"
            );
            // Keep signed in state, token will be refreshed when needed
            setIsSignedIn(true);
          } else {
            // No refresh token, clear expired token
            console.log("Token expired and no refresh token, clearing");
            localStorage.removeItem("google_drive_access_token");
            localStorage.removeItem("google_drive_token_expiry");
          }
        }
      } else {
        console.log("No stored token found");
      }
    };

    loadGoogleDriveToken();

    // Fetch OneDrive token from backend first
    const loadOneDriveToken = async () => {
      const backendTokenData = await fetchOneDriveTokenFromBackend();

      if (backendTokenData) {
        const now = new Date().getTime();
        const expiresAt = parseInt(backendTokenData.expires_at);

        // Check if token from backend is still valid
        if (now < expiresAt) {
          console.log(
            "Valid OneDrive token found in backend, restoring session"
          );
          // Update localStorage with token from backend (already saved in fetchOneDriveTokenFromBackend)
          setOneDriveAccessToken(backendTokenData.token);
          setIsOneDriveSignedIn(true);
          return; // Exit early, don't check localStorage
        } else {
          console.log("OneDrive token from backend is expired");
        }
      }

      // Fallback to localStorage if backend doesn't have a valid token
      const storedOneDriveToken = localStorage.getItem("onedrive_access_token");
      const oneDriveTokenExpiry = localStorage.getItem("onedrive_token_expiry");

      if (storedOneDriveToken && oneDriveTokenExpiry) {
        const now = new Date().getTime();
        const expiry = parseInt(oneDriveTokenExpiry);

        if (now < expiry) {
          // Token is still valid
          setOneDriveAccessToken(storedOneDriveToken);
          setIsOneDriveSignedIn(true);
        } else {
          // Token expired, try to refresh if refresh token exists
          const refreshToken = localStorage.getItem("onedrive_refresh_token");
          if (refreshToken) {
            console.log(
              "OneDrive token expired, but refresh token exists - will refresh on next use"
            );
            // Keep signed in state, token will be refreshed when needed
            setIsOneDriveSignedIn(true);
          } else {
            // No refresh token, clear expired token
            console.log(
              "OneDrive token expired and no refresh token, clearing"
            );
            localStorage.removeItem("onedrive_access_token");
            localStorage.removeItem("onedrive_token_expiry");
          }
        }
      }
    };

    loadOneDriveToken();

    // Check for existing SharePoint access token
    const storedSharePointToken = localStorage.getItem(
      "sharepoint_access_token"
    );
    const sharePointTokenExpiry = localStorage.getItem(
      "sharepoint_token_expiry"
    );

    if (storedSharePointToken && sharePointTokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(sharePointTokenExpiry);

      if (now < expiry) {
        // Token is still valid
        setSharePointAccessToken(storedSharePointToken);
        setIsSharePointSignedIn(true);
      } else {
        // Token expired, clear it
        localStorage.removeItem("sharepoint_access_token");
        localStorage.removeItem("sharepoint_token_expiry");
      }
    }

    // Load automated folders from localStorage
    const storedFolders = JSON.parse(
      localStorage.getItem("google_drive_automated_folders") || "[]"
    );
    setAutomatedFolders(storedFolders);

    // Load OneDrive automated folders from localStorage
    const storedOneDriveFolders = JSON.parse(
      localStorage.getItem("onedrive_automated_folders") || "[]"
    );
    setAutomatedOneDriveFolders(storedOneDriveFolders);
  }, [fetchGoogleDriveTokenFromBackend, fetchOneDriveTokenFromBackend]);

  // Fetch chatbot details when chatbotId is available
  useEffect(() => {
    const fetchChatbotDetails = async () => {
      if (!chatbotId) return;

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;
      const userId = user?.id || user?.user_id;

      if (!companyId || !token) return;

      try {
        const response = await fetch(ENDPOINTS.GET_CHATBOTS(companyId, userId), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const chatbot = Array.isArray(data)
            ? data.find((bot) => bot.id === chatbotId)
            : null;

          if (chatbot) {
            setChatbotName(chatbot.name || "");
          }
        }
      } catch (err) {
        console.error("Error fetching chatbot details:", err);
      }
    };

    fetchChatbotDetails();
  }, [chatbotId]);

  useEffect(() => {
    // If we're loading conversation, don't auto-scroll yet
    if (isLoadingConversation) {
      return;
    }

    // If we need to scroll to first message (from search)
    if (scrollToFirstRef.current && messages.length > 0) {
      const firstMessageId = messages[0].id;
      setTimeout(() => {
        const firstMessageRef = messagesRef.current[firstMessageId];
        if (firstMessageRef) {
          firstMessageRef.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          scrollToFirstRef.current = false; // Reset after scrolling
        }
      }, 200);
    } else {
      // Normal scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading, isLoadingConversation]);

  // Function to refresh access token using refresh token
  const refreshGoogleDriveToken = useCallback(async () => {
    const refreshToken = localStorage.getItem("google_drive_refresh_token");

    if (!refreshToken) {
      console.log("No refresh token available, user needs to re-authenticate");
      return null;
    }

    try {
      console.log("Refreshing Google Drive access token...");
      const tokenResponse = await fetch(GOOGLE_DRIVE_CONFIG.TOKEN_URI, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: GOOGLE_DRIVE_CONFIG.CLIENT_ID,
          client_secret: GOOGLE_DRIVE_CONFIG.CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: "refresh_token",
        }),
      });

      const tokenData = await tokenResponse.json();

      if (tokenData.access_token) {
        // Store new access token with expiry
        const expiry = new Date().getTime() + tokenData.expires_in * 1000;
        localStorage.setItem(
          "google_drive_access_token",
          tokenData.access_token
        );
        localStorage.setItem("google_drive_token_expiry", expiry.toString());

        // Save token to backend
        await saveGoogleDriveTokenToBackend(tokenData.access_token, expiry);

        // Log token refresh details
        const expiryDate = new Date(expiry);
        const minutesRemaining = (expiry - new Date().getTime()) / 1000 / 60;
        console.log("========================================");
        console.log("GOOGLE DRIVE TOKEN REFRESHED");
        console.log("========================================");
        console.log("New Access Token:", tokenData.access_token);
        console.log("Token Expires In:", tokenData.expires_in, "seconds");
        console.log("Token Expires At:", expiryDate.toISOString());
        console.log("Minutes Remaining:", minutesRemaining.toFixed(2));
        console.log("========================================");

        setAccessToken(tokenData.access_token);
        setIsSignedIn(true);
        return tokenData.access_token;
      } else {
        console.error("Failed to refresh token:", tokenData);
        // If refresh fails, clear tokens and require re-authentication
        localStorage.removeItem("google_drive_access_token");
        localStorage.removeItem("google_drive_token_expiry");
        localStorage.removeItem("google_drive_refresh_token");
        setIsSignedIn(false);
        setAccessToken(null);
        return null;
      }
    } catch (error) {
      console.error("Error refreshing Google Drive token:", error);
      // Clear tokens on error
      localStorage.removeItem("google_drive_access_token");
      localStorage.removeItem("google_drive_token_expiry");
      localStorage.removeItem("google_drive_refresh_token");
      setIsSignedIn(false);
      setAccessToken(null);
      return null;
    }
  }, [saveGoogleDriveTokenToBackend]);

  // Function to get valid access token (refresh if needed)
  const getValidGoogleDriveToken = useCallback(async () => {
    const storedToken = localStorage.getItem("google_drive_access_token");
    const tokenExpiry = localStorage.getItem("google_drive_token_expiry");

    if (!storedToken) {
      return null;
    }

    if (tokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(tokenExpiry);
      const timeUntilExpiry = expiry - now;
      const minutesUntilExpiry = timeUntilExpiry / 1000 / 60;

      // If token expires in less than 5 minutes, refresh it proactively
      if (minutesUntilExpiry < 5) {
        console.log(
          `Token expires in ${minutesUntilExpiry.toFixed(
            2
          )} minutes, refreshing...`
        );
        return await refreshGoogleDriveToken();
      }

      // If token is already expired, refresh it
      if (now >= expiry) {
        console.log("Token expired, refreshing...");
        return await refreshGoogleDriveToken();
      }
    }

    // Token is still valid
    return storedToken;
  }, [refreshGoogleDriveToken]);

  // Function to save OneDrive token to backend
  // USED IN: refreshOneDriveToken ~line 827, handleOneDriveCallback / picker sign-in ~line 2210
  // const saveOneDriveTokenToBackend = useCallback(async (accessToken, expiresAt, refreshToken) => { ... }, []);
  const saveOneDriveTokenToBackend = useCallback(async () => { }, []);

  // Function to refresh OneDrive access token using refresh token
  const refreshOneDriveToken = useCallback(async () => {
    const refreshToken = localStorage.getItem("onedrive_refresh_token");

    if (!refreshToken) {
      console.log(
        "No OneDrive refresh token available, user needs to re-authenticate"
      );
      return null;
    }

    try {
      console.log("Refreshing OneDrive access token...");
      const tokenResponse = await fetch(ONEDRIVE_CONFIG.TOKEN_URI, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          client_id: ONEDRIVE_CONFIG.APPLICATION_ID,
          client_secret: ONEDRIVE_CONFIG.CLIENT_SECRET,
          refresh_token: refreshToken,
          grant_type: "refresh_token",
        }),
      });

      const tokenData = await tokenResponse.json();

      if (tokenData.access_token) {
        // Store new access token with expiry
        const expiry = new Date().getTime() + tokenData.expires_in * 1000;
        localStorage.setItem("onedrive_access_token", tokenData.access_token);
        localStorage.setItem("onedrive_token_expiry", expiry.toString());

        // Update refresh token if a new one is provided
        const newRefreshToken = tokenData.refresh_token || refreshToken;
        if (tokenData.refresh_token) {
          localStorage.setItem(
            "onedrive_refresh_token",
            tokenData.refresh_token
          );
        }

        // Save token to backend
        await saveOneDriveTokenToBackend(
          tokenData.access_token,
          expiry,
          newRefreshToken
        );

        console.log("OneDrive token refreshed successfully");
        setOneDriveAccessToken(tokenData.access_token);
        setIsOneDriveSignedIn(true);
        return tokenData.access_token;
      } else {
        console.error("Failed to refresh OneDrive token:", tokenData);
        // If refresh fails, clear tokens and require re-authentication
        localStorage.removeItem("onedrive_access_token");
        localStorage.removeItem("onedrive_token_expiry");
        localStorage.removeItem("onedrive_refresh_token");
        setIsOneDriveSignedIn(false);
        setOneDriveAccessToken(null);
        return null;
      }
    } catch (error) {
      console.error("Error refreshing OneDrive token:", error);
      // Clear tokens on error
      localStorage.removeItem("onedrive_access_token");
      localStorage.removeItem("onedrive_token_expiry");
      localStorage.removeItem("onedrive_refresh_token");
      setIsOneDriveSignedIn(false);
      setOneDriveAccessToken(null);
      return null;
    }
  }, [saveOneDriveTokenToBackend]);

  // Function to get valid OneDrive access token (refresh if needed)
  const getValidOneDriveToken = useCallback(async () => {
    const storedToken = localStorage.getItem("onedrive_access_token");
    const tokenExpiry = localStorage.getItem("onedrive_token_expiry");

    if (!storedToken) {
      return null;
    }

    if (tokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(tokenExpiry);
      const timeUntilExpiry = expiry - now;
      const minutesUntilExpiry = timeUntilExpiry / 1000 / 60;

      // If token expires in less than 5 minutes, refresh it proactively
      if (minutesUntilExpiry < 5) {
        console.log(
          `OneDrive token expires in ${minutesUntilExpiry.toFixed(
            2
          )} minutes, refreshing...`
        );
        return await refreshOneDriveToken();
      }

      // If token is already expired, refresh it
      if (now >= expiry) {
        console.log("OneDrive token expired, refreshing...");
        return await refreshOneDriveToken();
      }
    }

    // Token is still valid
    return storedToken;
  }, [refreshOneDriveToken]);

  // Fetch files from a Google Drive folder
  const fetchFilesFromFolder = useCallback(async (folderId, accessToken) => {
    try {
      // Use Google Drive API to list files in folder
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&fields=files(id,name,mimeType,size,webViewLink,modifiedTime)`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch folder: ${response.statusText}`);
      }

      const data = await response.json();
      return data.files || [];
    } catch (error) {
      console.error(`Error fetching files from folder ${folderId}:`, error);
      throw error;
    }
  }, []);

  // Fetch existing documents to check for duplicates
  const getExistingDocuments = useCallback(async (companyId, token) => {
    try {
      const response = await fetch(ENDPOINTS.GET_DOCUMENTS(companyId), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.warn("Failed to fetch existing documents for deduplication");
        return [];
      }

      const data = await response.json();
      return data.documents || [];
    } catch (error) {
      console.error("Error fetching existing documents:", error);
      return [];
    }
  }, []);

  // Ingest files from all automated folders
  const ingestFilesFromAutomatedFolders = useCallback(async () => {
    // Prevent concurrent runs
    if (isIngestingGoogleDriveRef.current) {
      console.log("Google Drive ingestion already in progress, skipping...");
      return;
    }

    if (automatedFolders.length === 0) {
      console.log("No automated folders configured");
      return;
    }

    isIngestingGoogleDriveRef.current = true;

    try {
      const validToken = await getValidGoogleDriveToken();
      if (!validToken) {
        console.error("No valid access token for automated ingestion");
        return;
      }

      console.log(
        `Starting automated ingestion for ${automatedFolders.length} folder(s)...`
      );

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;

      if (!companyId || !token) {
        console.error("Missing company ID or token for automated ingestion");
        return;
      }

      // Fetch existing documents to check for duplicates
      const existingDocs = await getExistingDocuments(companyId, token);

      // Create a set of existing file names and Google Drive IDs for quick lookup
      const existingFileNames = new Set(
        existingDocs.map((doc) => doc.name?.toLowerCase())
      );

      // Also track ingested Google Drive file IDs from localStorage
      const ingestedFileIds = JSON.parse(
        localStorage.getItem("google_drive_ingested_file_ids") || "[]"
      );
      const ingestedFileIdsSet = new Set(ingestedFileIds);

      let totalIngested = 0;
      let totalSkipped = 0;
      const updatedFolders = [...automatedFolders];
      const newlyIngestedIds = [];

      for (let i = 0; i < automatedFolders.length; i++) {
        const folder = automatedFolders[i];
        try {
          console.log(`Processing folder: ${folder.name} (${folder.id})`);

          // Fetch files from folder
          const files = await fetchFilesFromFolder(folder.id, validToken);

          // Filter allowed file types
          const allowedMimeTypes = [
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/pdf",
            "image/jpeg",
            "image/png",
            "application/vnd.ms-excel",
            "text/plain",
            "application/vnd.oasis.opendocument.text",
            "application/vnd.google-apps.document",
            "application/vnd.google-apps.spreadsheet",
            "application/vnd.google-apps.presentation",
          ];

          const validFiles = files.filter((file) =>
            allowedMimeTypes.includes(file.mimeType)
          );

          if (validFiles.length === 0) {
            console.log(`No valid files found in folder: ${folder.name}`);
            continue;
          }

          // Filter out already ingested files
          const newFiles = validFiles.filter((file) => {
            const fileId = file.id;
            const fileName = file.name?.toLowerCase();

            // Check if file ID was already ingested
            if (ingestedFileIdsSet.has(fileId)) {
              console.log(
                `Skipping already ingested file: ${file.name} (ID: ${fileId})`
              );
              return false;
            }

            // Check if file name already exists in documents
            if (existingFileNames.has(fileName)) {
              console.log(`Skipping duplicate file name: ${file.name}`);
              return false;
            }

            return true;
          });

          if (newFiles.length === 0) {
            console.log(`No new files to ingest from folder: ${folder.name}`);
            totalSkipped += validFiles.length;
            continue;
          }

          console.log(
            `Found ${newFiles.length} new file(s) out of ${validFiles.length} total in folder: ${folder.name}`
          );

          // Upload only new files
          for (const file of newFiles) {
            const fileData = {
              id: file.id,
              name: file.name,
              mimeType: file.mimeType,
              size: file.size,
              url: file.webViewLink,
            };

            const success = await uploadGoogleDriveFile(fileData);
            if (success) {
              totalIngested++;
              newlyIngestedIds.push(file.id);
              // Add to existing file names set to avoid duplicates in same batch
              existingFileNames.add(file.name?.toLowerCase());
            }
          }

          totalSkipped += validFiles.length - newFiles.length;

          // Update last ingested timestamp
          updatedFolders[i].lastIngestedAt = new Date().toISOString();
          console.log(
            `Ingested ${newFiles.length} new file(s) from folder: ${folder.name} (${totalSkipped} skipped)`
          );
        } catch (error) {
          console.error(`Error ingesting folder ${folder.name}:`, error);
        }
      }

      // Update ingested file IDs in localStorage
      if (newlyIngestedIds.length > 0) {
        const updatedIngestedIds = [...ingestedFileIds, ...newlyIngestedIds];
        localStorage.setItem(
          "google_drive_ingested_file_ids",
          JSON.stringify(updatedIngestedIds)
        );
      }

      // Update folders with new timestamps
      setAutomatedFolders(updatedFolders);
      localStorage.setItem(
        "google_drive_automated_folders",
        JSON.stringify(updatedFolders)
      );

      if (totalIngested > 0) {
        console.log(
          `Automated ingestion completed: ${totalIngested} new file(s) ingested, ${totalSkipped} skipped`
        );
      } else if (totalSkipped > 0) {
        console.log(
          `Automated ingestion completed: All ${totalSkipped} file(s) were already ingested`
        );
      }
    } finally {
      isIngestingGoogleDriveRef.current = false;
    }
  }, [
    automatedFolders,
    getValidGoogleDriveToken,
    fetchFilesFromFolder,
    getExistingDocuments,
  ]);

  // Store latest function reference
  useEffect(() => {
    ingestGoogleDriveRef.current = ingestFilesFromAutomatedFolders;
  }, [ingestFilesFromAutomatedFolders]);

  // Fetch files from a OneDrive folder
  const fetchFilesFromOneDriveFolder = useCallback(
    async (folderId, accessToken) => {
      try {
        const endpoint =
          folderId === "root"
            ? "https://graph.microsoft.com/v1.0/me/drive/root/children"
            : `https://graph.microsoft.com/v1.0/me/drive/items/${folderId}/children`;

        const response = await fetch(
          `${endpoint}?$select=id,name,size,file,folder,@microsoft.graph.downloadUrl,webUrl`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch OneDrive folder: ${response.statusText}`
          );
        }

        const data = await response.json();
        // Filter out folders, only return files
        return (data.value || []).filter((item) => item.file !== null);
      } catch (error) {
        console.error(
          `Error fetching files from OneDrive folder ${folderId}:`,
          error
        );
        throw error;
      }
    },
    []
  );

  // Upload OneDrive file
  const uploadOneDriveFile = async (file, oneDriveToken) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.user_id || user?.id;

    if (!companyId || !token) {
      showErrorToast("Missing authentication details");
      return false;
    }

    if (!oneDriveToken) {
      showErrorToast(
        "OneDrive access token not available. Please sign in again."
      );
      return false;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        // Note: Authorization header removed - backend doesn't require it
        // Note: user_id and company_id are included in requestBody
      };

      // Prepare file data with access token - same structure as manual picker
      // Spread the file object and add user_id, company_id, and access_token
      const requestBody = {
        ...file,
        user_id: userId,
        company_id: companyId,
        access_token: oneDriveToken,
      };

      // Debug: Log request details
      console.log("         🔐 Request Details:", {
        hasUserId: !!userId,
        userId: userId,
        hasCompanyId: !!companyId,
        companyId: companyId,
        hasOneDriveToken: !!oneDriveToken,
      });

      // Check if chatbot_id is present in localStorage
      const activeChatbotId = localStorage.getItem("active_chatbot_id");

      // Use chatbot-specific endpoint if chatbot_id is available, otherwise use regular endpoint
      const endpoint = activeChatbotId
        ? ENDPOINTS.UPLOAD_DOCUMENT_ONEDRIVE_CHATBOT(companyId, activeChatbotId)
        : ENDPOINTS.UPLOAD_DOCUMENT_ONEDRIVE(companyId);

      console.log("         ╔════════════════════════════════════════════╗");
      console.log("         ║  UPLOADING TO BACKEND API                  ║");
      console.log("         ╚════════════════════════════════════════════╝");
      console.log(`         📡 Endpoint: ${endpoint}`);
      console.log(`         📄 File: ${file.name}`);
      console.log(`         🆔 Company ID: ${companyId}`);
      console.log(`         👤 User ID: ${userId}`);
      console.log(`         🔑 Token: ${oneDriveToken.substring(0, 20)}...`);
      console.log("         📦 Request Body Summary:", {
        fileName: file.name,
        fileId: file.id,
        fileSize: file.size,
        mimeType: file.file?.mimeType,
        hasDownloadUrl: !!file["@microsoft.graph.downloadUrl"],
        hasWebUrl: !!file.webUrl,
        hasAccessToken: !!requestBody.access_token,
        hasUserId: !!requestBody.user_id,
        hasCompanyId: !!requestBody.company_id,
      });
      console.log(
        "         📦 Full Request Body Structure:",
        JSON.stringify(
          {
            "@odata.context": file["@odata.context"] || "N/A",
            "@odata.etag": file["@odata.etag"] || "N/A",
            id: file.id,
            name: file.name,
            webUrl: file.webUrl,
            size: file.size,
            file: file.file,
            "@microsoft.graph.downloadUrl": file["@microsoft.graph.downloadUrl"]
              ? "Present"
              : "Missing",
            user_id: userId,
            company_id: companyId,
            access_token: "Present (hidden for security)",
          },
          null,
          2
        )
      );

      const uploadStartTime = Date.now();
      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });
      const uploadDuration = Date.now() - uploadStartTime;

      const data = await response.json();

      console.log(
        `         📊 Response Status: ${response.status} ${response.statusText}`
      );
      console.log(`         ⏱️  Upload Duration: ${uploadDuration}ms`);

      // Treat non-empty `error` from API as failure even if HTTP status is 200
      const rawError =
        data && Object.prototype.hasOwnProperty.call(data, "error")
          ? data.error
          : null;
      const normalizedError =
        typeof rawError === "string" ? rawError.trim() : rawError || "";
      const hasApiError = !!normalizedError;

      if (!response.ok || hasApiError) {
        console.log(`         ❌ UPLOAD FAILED!`);
        console.log(`         📝 Error Details:`, data);

        const message =
          (data && (data.message || data.detail)) ||
          normalizedError ||
          `Failed to upload "${file.name}" ❌`;

        showErrorToast(message);
        return false;
      }

      const successMessage =
        (data && (data.message || data.detail)) ||
        `File "${file.name}" uploaded successfully ✅`;

      showSuccessToast(successMessage);

      console.log(`         ✅ UPLOAD SUCCESSFUL!`);
      console.log(`         📝 Backend Response:`, data);
      return true;
    } catch (error) {
      console.error(`Error uploading OneDrive file ${file.name}:`, error);
      showErrorToast(`Failed to upload "${file.name}". Please try again.`);
      return false;
    }
  };

  // Helper function to get download URL when missing
  // Returns the /content endpoint URL directly - backend will handle the redirect
  const getOneDriveDownloadUrl = useCallback((fileId) => {
    // Return the /content endpoint URL directly
    // The backend can follow the redirect to get the actual download URL
    // This avoids making unnecessary HEAD requests
    return `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}/content`;
  }, []);

  // Ingest files from all automated OneDrive folders
  const ingestFilesFromAutomatedOneDriveFolders = useCallback(async () => {
    // Prevent concurrent runs
    if (isIngestingOneDriveRef.current) {
      console.log("OneDrive ingestion already in progress, skipping...");
      return;
    }

    if (automatedOneDriveFolders.length === 0) {
      console.log("No automated OneDrive folders configured");
      return;
    }

    // Get valid token (refresh if needed)
    const validOneDriveToken = await getValidOneDriveToken();
    if (!validOneDriveToken) {
      console.error("No valid OneDrive access token for automated ingestion");
      return;
    }

    isIngestingOneDriveRef.current = true;

    try {
      console.log("═══════════════════════════════════════════════════════");
      console.log("🤖 AUTOMATED ONEDRIVE FOLDER INGESTION STARTED");
      console.log("═══════════════════════════════════════════════════════");
      console.log(
        `📁 Total Folders to Process: ${automatedOneDriveFolders.length}`
      );
      console.log(`⏰ Started at: ${new Date().toLocaleString()}`);
      console.log("───────────────────────────────────────────────────────");

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;

      if (!companyId || !token) {
        console.error("Missing company ID or token for automated ingestion");
        return;
      }

      // Fetch existing documents to check for duplicates
      const existingDocs = await getExistingDocuments(companyId, token);

      // Create a set of existing file names and OneDrive IDs for quick lookup
      const existingFileNames = new Set(
        existingDocs.map((doc) => doc.name?.toLowerCase())
      );

      // Also track ingested OneDrive file IDs from localStorage
      const ingestedFileIds = JSON.parse(
        localStorage.getItem("onedrive_ingested_file_ids") || "[]"
      );
      const ingestedFileIdsSet = new Set(ingestedFileIds);

      let totalIngested = 0;
      let totalSkipped = 0;
      const updatedFolders = [...automatedOneDriveFolders];
      const newlyIngestedIds = [];

      for (let i = 0; i < automatedOneDriveFolders.length; i++) {
        const folder = automatedOneDriveFolders[i];
        try {
          console.log(
            `\n📂 [${i + 1}/${automatedOneDriveFolders.length
            }] Processing Folder: "${folder.name}"`
          );
          console.log(`   🆔 Folder ID: ${folder.id}`);
          console.log(`   🔗 Folder URL: ${folder.url || "N/A"}`);
          console.log(`   📥 Fetching files from OneDrive...`);

          // Fetch files from folder
          const files = await fetchFilesFromOneDriveFolder(
            folder.id,
            validOneDriveToken
          );

          console.log(`   ✅ Found ${files.length} total item(s) in folder`);

          // Filter allowed file types
          const allowedMimeTypes = [
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/pdf",
            "image/jpeg",
            "image/png",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/plain",
            "application/vnd.oasis.opendocument.text",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          ];

          const validFiles = files.filter((file) =>
            allowedMimeTypes.includes(file.file?.mimeType || "")
          );

          console.log(`   🔍 Filtering files by allowed types...`);
          console.log(`   ✅ Valid files: ${validFiles.length}`);
          console.log(
            `   ❌ Skipped files: ${files.length - validFiles.length}`
          );

          if (validFiles.length === 0) {
            console.log(
              `   ⚠️  No valid files found in folder: ${folder.name}`
            );
            console.log(
              `   ℹ️  Allowed types: DOC, DOCX, PDF, XLS, XLSX, TXT, ODT, PPTX`
            );
            continue;
          }

          // Filter out already ingested files
          const newFiles = validFiles.filter((file) => {
            const fileId = file.id;
            const fileName = file.name?.toLowerCase();

            // Check if file ID was already ingested
            if (ingestedFileIdsSet.has(fileId)) {
              console.log(
                `Skipping already ingested file: ${file.name} (ID: ${fileId})`
              );
              return false;
            }

            // Check if file name already exists in documents
            if (existingFileNames.has(fileName)) {
              console.log(`Skipping duplicate file name: ${file.name}`);
              return false;
            }

            return true;
          });

          console.log(`   🔄 Checking for duplicates...`);
          console.log(`   🆕 New files to upload: ${newFiles.length}`);
          console.log(
            `   ⏭️  Already ingested: ${validFiles.length - newFiles.length}`
          );

          if (newFiles.length === 0) {
            console.log(
              `   ℹ️  All files already ingested from folder: ${folder.name}`
            );
            totalSkipped += validFiles.length;
            continue;
          }

          console.log(
            `   \n   📤 Starting upload of ${newFiles.length} new file(s)...`
          );

          // Upload only new files
          for (let fileIndex = 0; fileIndex < newFiles.length; fileIndex++) {
            const file = newFiles[fileIndex];
            try {
              console.log(
                `   \n   📄 [${fileIndex + 1}/${newFiles.length}] File: "${file.name
                }"`
              );
              console.log(`      🆔 ID: ${file.id}`);
              console.log(
                `      📊 Size: ${file.size
                  ? (file.size / 1024 / 1024).toFixed(2) + " MB"
                  : "Unknown"
                }`
              );
              console.log(`      📝 Type: ${file.file?.mimeType || "Unknown"}`);
              console.log(`      ⬇️  Fetching download URL...`);

              // Fetch full file details including @microsoft.graph.downloadUrl (same as manual picker)
              const fileDetailsResponse = await fetch(
                `https://graph.microsoft.com/v1.0/me/drive/items/${file.id}?$select=id,name,size,file,@microsoft.graph.downloadUrl,webUrl`,
                {
                  headers: {
                    Authorization: `Bearer ${validOneDriveToken}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (!fileDetailsResponse.ok) {
                console.error(`      ❌ Failed to fetch file details`);
                continue;
              }

              const fileDetails = await fileDetailsResponse.json();

              // If downloadUrl is missing, use the /content endpoint URL directly
              // Backend will handle the redirect
              if (!fileDetails["@microsoft.graph.downloadUrl"]) {
                console.log(
                  `      ⚠️  Download URL missing, using /content endpoint URL`
                );
                fileDetails["@microsoft.graph.downloadUrl"] =
                  getOneDriveDownloadUrl(fileDetails.id);
                console.log(
                  `      ✅ Using /content endpoint URL (backend will handle redirect)`
                );
              } else {
                console.log(`      ✅ Download URL obtained`);
              }

              // Use the same structure as manual picker - spread the file object
              // This includes all @odata fields, id, name, webUrl, size, file object, @microsoft.graph.downloadUrl
              const fileData = {
                ...fileDetails,
              };

              console.log(
                `      🚀 Sending to backend: UPLOAD_DOCUMENT_ONEDRIVE`
              );
              console.log(`      📦 File Data Structure:`, {
                hasODataContext: !!fileData["@odata.context"],
                hasODataEtag: !!fileData["@odata.etag"],
                id: fileData.id,
                name: fileData.name,
                size: fileData.size,
                hasWebUrl: !!fileData.webUrl,
                mimeType: fileData.file?.mimeType,
                hasDownloadUrl: !!fileData["@microsoft.graph.downloadUrl"],
              });

              const success = await uploadOneDriveFile(
                fileData,
                validOneDriveToken
              );
              if (success) {
                console.log(`      ✅ SUCCESS! File uploaded to backend`);
                totalIngested++;
                newlyIngestedIds.push(file.id);
                // Add to existing file names set to avoid duplicates in same batch
                existingFileNames.add(file.name?.toLowerCase());
              } else {
                console.log(`      ❌ FAILED! Upload to backend unsuccessful`);
              }
            } catch (error) {
              console.error(`      ❌ ERROR processing file:`, error);
            }
          }

          totalSkipped += validFiles.length - newFiles.length;

          // Update last ingested timestamp
          updatedFolders[i].lastIngestedAt = new Date().toISOString();

          console.log(`   \n   📊 Folder Summary:`);
          console.log(`      ✅ Uploaded: ${newFiles.length} file(s)`);
          console.log(
            `      ⏭️  Skipped: ${validFiles.length - newFiles.length} file(s)`
          );
          console.log(
            `      ⏰ Completed at: ${new Date().toLocaleTimeString()}`
          );
          console.log(
            `   ───────────────────────────────────────────────────────`
          );
        } catch (error) {
          console.error(
            `   ❌ ERROR processing folder "${folder.name}":`,
            error
          );
          console.log(
            `   ───────────────────────────────────────────────────────`
          );
        }
      }

      // Update ingested file IDs in localStorage
      if (newlyIngestedIds.length > 0) {
        const updatedIngestedIds = [...ingestedFileIds, ...newlyIngestedIds];
        localStorage.setItem(
          "onedrive_ingested_file_ids",
          JSON.stringify(updatedIngestedIds)
        );
      }

      // Update folders with new timestamps
      setAutomatedOneDriveFolders(updatedFolders);
      localStorage.setItem(
        "onedrive_automated_folders",
        JSON.stringify(updatedFolders)
      );

      console.log("\n═══════════════════════════════════════════════════════");
      console.log("🏁 AUTOMATED ONEDRIVE INGESTION COMPLETED");
      console.log("═══════════════════════════════════════════════════════");
      console.log(`📊 FINAL SUMMARY:`);
      console.log(`   ✅ Total Files Uploaded: ${totalIngested}`);
      console.log(`   ⏭️  Total Files Skipped: ${totalSkipped}`);
      console.log(
        `   📁 Folders Processed: ${automatedOneDriveFolders.length}`
      );
      console.log(`   ⏰ Finished at: ${new Date().toLocaleString()}`);

      if (totalIngested > 0) {
        console.log(
          `   🎉 ${totalIngested} new file(s) successfully sent to backend!`
        );
      } else if (totalSkipped > 0) {
        console.log(`   ℹ️  All ${totalSkipped} file(s) were already ingested`);
      } else {
        console.log(`   ⚠️  No files found to process`);
      }
      console.log("═══════════════════════════════════════════════════════\n");
    } finally {
      isIngestingOneDriveRef.current = false;
    }
  }, [
    automatedOneDriveFolders,
    getValidOneDriveToken,
    fetchFilesFromOneDriveFolder,
    getExistingDocuments,
  ]);

  // Store latest function reference
  useEffect(() => {
    ingestOneDriveRef.current = ingestFilesFromAutomatedOneDriveFolders;
  }, [ingestFilesFromAutomatedOneDriveFolders]);

  // Cron job: Run automated ingestion every 12 hours
  useEffect(() => {
    if (automatedFolders.length === 0) {
      return; // No folders to process
    }

    console.log(
      `Setting up automated ingestion cron job (12 hours interval) for ${automatedFolders.length} folder(s)`
    );

    // Run immediately on mount if folders exist (only once)
    if (ingestGoogleDriveRef.current) {
      ingestGoogleDriveRef.current();
    }

    // Set up interval to run every 12 hours
    const intervalId = setInterval(() => {
      console.log("Running scheduled automated ingestion...");
      if (ingestGoogleDriveRef.current) {
        ingestGoogleDriveRef.current();
      }
    }, 12 * 60 * 60 * 1000); // 12 hours

    return () => {
      clearInterval(intervalId);
    };
  }, [automatedFolders.length]); // Only re-run when folder count changes

  // Cron job: Run automated OneDrive ingestion every 12 hours
  useEffect(() => {
    if (automatedOneDriveFolders.length === 0) {
      return; // No folders to process
    }

    // Check if user is signed in (we'll refresh token if needed during ingestion)
    if (!isOneDriveSignedIn) {
      return; // User not signed in to OneDrive
    }

    console.log(
      `Setting up automated OneDrive ingestion cron job (12 hours interval) for ${automatedOneDriveFolders.length} folder(s)`
    );

    // Run immediately on mount if folders exist (only once)
    if (ingestOneDriveRef.current) {
      ingestOneDriveRef.current();
    }

    // Set up interval to run every 12 hours
    const intervalId = setInterval(() => {
      console.log("Running scheduled automated OneDrive ingestion...");
      if (ingestOneDriveRef.current) {
        ingestOneDriveRef.current();
      }
    }, 12 * 60 * 60 * 1000); // 12 hours

    return () => {
      clearInterval(intervalId);
    };
  }, [automatedOneDriveFolders.length, isOneDriveSignedIn]); // Only re-run when folder count or sign-in status changes

  // Load Google Drive Picker API
  useEffect(() => {
    const loadGoogleDriveAPI = () => {
      // Load Google Identity Services first
      if (!window.google) {
        const gisScript = document.createElement("script");
        gisScript.src = "https://accounts.google.com/gsi/client";
        gisScript.onload = () => {
          // Load GAPI for picker
          if (!window.gapi) {
            const gapiScript = document.createElement("script");
            gapiScript.src = "https://apis.google.com/js/api.js";
            gapiScript.onload = () => {
              window.gapi.load("picker", () => {
                setPickerApiLoaded(true);
              });
            };
            document.head.appendChild(gapiScript);
          } else {
            window.gapi.load("picker", () => {
              setPickerApiLoaded(true);
            });
          }
          setGapiLoaded(true);
        };
        document.head.appendChild(gisScript);
      } else {
        // Load GAPI for picker
        if (!window.gapi) {
          const gapiScript = document.createElement("script");
          gapiScript.src = "https://apis.google.com/js/api.js";
          gapiScript.onload = () => {
            window.gapi.load("picker", () => {
              setPickerApiLoaded(true);
            });
          };
          document.head.appendChild(gapiScript);
        } else {
          window.gapi.load("picker", () => {
            setPickerApiLoaded(true);
          });
        }
        setGapiLoaded(true);
      }
    };

    loadGoogleDriveAPI();
  }, []);

  // Load OneDrive Picker SDK
  useEffect(() => {
    if (window.OneDrive) {
      setOneDriveReady(true);
      return;
    }
    const sdkScript = document.createElement("script");
    sdkScript.src = "https://js.live.net/v7.2/OneDrive.js";
    sdkScript.async = true;
    sdkScript.onload = () => {
      console.log("OneDrive SDK loaded");
      setOneDriveReady(true);
    };
    sdkScript.onerror = () => {
      showErrorToast("Failed to load OneDrive SDK");
    };
    document.head.appendChild(sdkScript);
  }, []);

  const openGoogleDrivePickerWithToken = useCallback(
    async (token, folderMode = false) => {
      console.log(
        "Opening picker with token:",
        token ? "Token present" : "No token",
        folderMode ? "(Folder Mode)" : "(File Mode)"
      );

      if (!token) {
        showErrorToast("No access token available. Please sign in again.");
        return;
      }

      // Log token being used for picker and validate token before using it
      const tokenExpiry = localStorage.getItem("google_drive_token_expiry");
      if (tokenExpiry) {
        const now = new Date().getTime();
        const expiry = parseInt(tokenExpiry);
        const minutesRemaining = (expiry - now) / 1000 / 60;

        // Log token details
        console.log("========================================");
        console.log("USING GOOGLE DRIVE TOKEN FOR PICKER");
        console.log("========================================");
        console.log("Access Token:", token);
        console.log("Token Expires At:", new Date(expiry).toISOString());
        console.log("Minutes Remaining:", minutesRemaining.toFixed(2));
        console.log("========================================");

        if (now >= expiry) {
          // Token expired, try to refresh
          const refreshedToken = await refreshGoogleDriveToken();
          if (refreshedToken) {
            token = refreshedToken; // Use refreshed token
          } else {
            showErrorToast(
              "Access token expired and refresh failed. Please sign in again."
            );
            localStorage.removeItem("google_drive_access_token");
            localStorage.removeItem("google_drive_token_expiry");
            localStorage.removeItem("google_drive_refresh_token");
            setIsSignedIn(false);
            setAccessToken(null);
            return;
          }
        }
      }

      try {
        console.log("Creating Google Drive picker...");

        // Google Drive Connect + Folder Picker flow commented out — skip folder mode
        if (folderMode) return;

        {
          // File selection mode (existing flow)
          // Define allowed MIME types for the picker
          const allowedMimeTypes = [
            "application/msword", // DOC
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOCX
            "application/pdf", // PDF
            "image/jpeg",
            "image/png",
            "application/vnd.ms-excel", // XLS
            "text/plain", // TXT
            "application/vnd.oasis.opendocument.text", // ODT
            "application/vnd.google-apps.document", // Google Docs
            "application/vnd.google-apps.spreadsheet",
            "application/vnd.google-apps.presentation",
          ];

          // Create a single view with all allowed file types
          const docsView = new window.google.picker.DocsView()
            .setIncludeFolders(false)
            .setSelectFolderEnabled(false)
            .setMimeTypes(allowedMimeTypes.join(","));

          const picker = new window.google.picker.PickerBuilder()
            .addView(docsView)
            .setOAuthToken(token)
            .setCallback(handleGoogleDriveFilePicked)
            .setTitle("Select document(s)")
            .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
            .build();

          console.log("Picker created, making it visible...");
          picker.setVisible(true);
        }
      } catch (error) {
        console.error("Error creating Google Drive picker:", error);
        showErrorToast("Failed to open Google Drive picker. Please try again.");
      }
    },
    [refreshGoogleDriveToken]
  );

  const openGoogleDrivePicker = useCallback(
    async (folderMode = false) => {
      // Get valid token (refresh if needed)
      const validToken = await getValidGoogleDriveToken();

      if (!validToken) {
        showErrorToast("No access token available. Please sign in again.");
        // Clear any stored tokens and reset state
        localStorage.removeItem("google_drive_access_token");
        localStorage.removeItem("google_drive_token_expiry");
        localStorage.removeItem("google_drive_refresh_token");
        setIsSignedIn(false);
        setAccessToken(null);
        return;
      }

      openGoogleDrivePickerWithToken(validToken, folderMode);
    },
    [getValidGoogleDriveToken, openGoogleDrivePickerWithToken]
  );

  // OAuth callback: URL params provider, status, error; for Google PKCE also code + state
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get("provider");
    const status = params.get("status");
    const error = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    if (provider && error) {
      showErrorToast(`${provider === "onedrive" || provider === "sharepoint" ? "OneDrive / SharePoint" : "Google Drive"} OAuth error: ${error}`);
      if (provider === "google_drive") localStorage.removeItem("google_drive_code_verifier");
      window.history.replaceState({}, "", window.location.pathname);
    }

    const run = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user?.company_id;

      if (provider === "google_drive" && code && state && companyId) {
        const codeVerifier = localStorage.getItem("google_drive_code_verifier");
        localStorage.removeItem("google_drive_code_verifier");
        window.history.replaceState({}, "", window.location.pathname);
        if (!codeVerifier) {
          showErrorToast("Google Drive: missing code verifier. Please try connecting again.");
          return;
        }
        try {
          const res = await fetch(ENDPOINTS.GOOGLE_OAUTH_EXCHANGE(companyId), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, state, code_verifier: codeVerifier }),
          });
          const data = await res.json();
          if (!res.ok) {
            showErrorToast(data.detail || data.message || "Google Drive token exchange failed");
            return;
          }
          showSuccessToast("Google Drive connected successfully.");
          const tokenData = await fetchGoogleDriveTokenFromBackend();
          if (tokenData) {
            const now = new Date().getTime();
            const exp = parseInt(tokenData.expires_at);
            if (now < exp) {
              localStorage.setItem("google_drive_access_token", tokenData.token);
              localStorage.setItem("google_drive_token_expiry", tokenData.expires_at.toString());
              setAccessToken(tokenData.token);
              setIsSignedIn(true);
            }
          }
        } catch (err) {
          console.error("Google OAuth exchange error:", err);
          showErrorToast("Google Drive token exchange failed");
        }
        return;
      }

      if (provider && status === "success") {
        showSuccessToast(
          provider === "onedrive"
            ? "OneDrive connected successfully."
            : provider === "sharepoint"
            ? "SharePoint connected successfully."
            : "Google Drive connected successfully."
        );
        window.history.replaceState({}, "", window.location.pathname);
      }

      if (provider === "sharepoint" && status === "success") {
        setSharePointCompanyConnected(true);
      }

      if (provider === "google_drive" && status === "success" && !code && companyId) {
        const data = await fetchGoogleDriveTokenFromBackend();
        if (data) {
          const now = new Date().getTime();
          const exp = parseInt(data.expires_at);
          if (now < exp) {
            localStorage.setItem("google_drive_access_token", data.token);
            localStorage.setItem("google_drive_token_expiry", data.expires_at.toString());
            setAccessToken(data.token);
            setIsSignedIn(true);
          }
        }
      } else if (provider === "onedrive" && status === "success" && companyId) {
        window.history.replaceState({}, "", window.location.pathname);
        const data = await fetchOneDriveTokenFromBackend();
        if (data) {
          const now = new Date().getTime();
          const exp = parseInt(data.expires_at);
          if (now < exp) {
            localStorage.setItem("onedrive_access_token", data.token);
            localStorage.setItem("onedrive_token_expiry", data.expires_at.toString());
            if (data.refresh_token) localStorage.setItem("onedrive_refresh_token", data.refresh_token);
            setOneDriveAccessToken(data.token);
            setIsOneDriveSignedIn(true);
          }
        }
      } else if (!provider) {
        const flag = localStorage.getItem("google_drive_auth_redirect");
        if (flag) {
          localStorage.removeItem("google_drive_auth_redirect");
          const data = await fetchGoogleDriveTokenFromBackend();
          if (data) {
            const now = new Date().getTime();
            const exp = parseInt(data.expires_at);
            if (now < exp) {
              localStorage.setItem("google_drive_access_token", data.token);
              localStorage.setItem("google_drive_token_expiry", data.expires_at.toString());
              setAccessToken(data.token);
              setIsSignedIn(true);
            }
          }
        }
      }
    };
    run();
  }, [location.search, fetchGoogleDriveTokenFromBackend, fetchOneDriveTokenFromBackend]);

  const showOneDriveFilePicker = useCallback((files) => {
    // Create a simple modal for file selection
    const modal = document.createElement("div");
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    `;

    const modalContent = document.createElement("div");
    modalContent.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 20px;
      max-width: 500px;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    modalContent.innerHTML = `
      <h3 style="margin: 0 0 15px 0; color: #333;">Select a file from OneDrive</h3>
      <div id="file-list" style="max-height: 300px; overflow-y: auto;">
        ${files
        .map(
          (file) => `
          <div style="padding: 10px; border: 1px solid #ddd; margin: 5px 0; cursor: pointer; border-radius: 4px; hover:background: #f5f5f5;" 
               onclick="window.selectOneDriveFile('${file.id}', '${file.name
            }', '${file.file?.mimeType || ""}', '${file.size || 0}')">
            <strong>${file.name}</strong><br>
            <small style="color: #666;">${file.file?.mimeType || "Unknown type"
            } • ${file.size
              ? (file.size / 1024 / 1024).toFixed(2) + " MB"
              : "Unknown size"
            }</small>
          </div>
        `
        )
        .join("")}
      </div>
      <div style="margin-top: 15px; text-align: right;">
        <button onclick="window.closeOneDriveModal()" style="padding: 8px 16px; margin-left: 10px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer;">Cancel</button>
      </div>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);

    // Add global functions for the modal
    window.selectOneDriveFile = (fileId, fileName, mimeType, fileSize) => {
      handleOneDriveFileSelected({
        id: fileId,
        name: fileName,
        mimeType: mimeType,
        size: parseInt(fileSize),
      });
      window.closeOneDriveModal();
    };

    window.closeOneDriveModal = () => {
      document.body.removeChild(modal);
      delete window.selectOneDriveFile;
      delete window.closeOneDriveModal;
    };
  }, []);

  const openOneDrivePicker = useCallback(async () => {
    if (!oneDriveAccessToken) {
      showErrorToast(
        "No OneDrive access token available. Please sign in again."
      );
      return;
    }

    try {
      console.log("Fetching OneDrive files...");

      // Fetch files from OneDrive root
      const response = await fetch(
        `${ONEDRIVE_CONFIG.GRAPH_API_BASE}/me/drive/root/children?$filter=file ne null`,
        {
          headers: {
            Authorization: `Bearer ${oneDriveAccessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const files = data.value || [];

      // Filter files by allowed types
      const allowedMimeTypes = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/pdf",
            "image/jpeg",
            "image/png",
        "application/vnd.ms-excel",

        "text/plain",
        "application/vnd.oasis.opendocument.text",
        "application/vnd.google-apps.document",
        "application/vnd.google-apps.spreadsheet",
        "application/vnd.google-apps.presentation",
      ];

      const filteredFiles = files.filter((file) =>
        allowedMimeTypes.includes(file.file?.mimeType || "")
      );

      if (filteredFiles.length === 0) {
        showErrorToast(
          "No supported files found in OneDrive. Please upload DOC, DOCX, PDF, or XLSX files."
        );
        return;
      }

      // Create a simple file picker modal
      showOneDriveFilePicker(filteredFiles);
    } catch (error) {
      console.error("Error fetching OneDrive files:", error);
      showErrorToast("Failed to fetch OneDrive files. Please try again.");
    }
  }, [oneDriveAccessToken, showOneDriveFilePicker]);

  const handleOneDriveFileSelected = (file) => {
    console.log("=== ONEDRIVE FILE SELECTED ===");
    console.log("File ID:", file.id);
    console.log("File Name:", file.name);
    console.log("MIME Type:", file.mimeType);
    console.log(
      "File Size:",
      file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Unknown"
    );
    console.log("Full File Object:", file);
    console.log("=== END FILE DETAILS ===");

    showSuccessToast(`File selected: ${file.name}`);
  };

  // SharePoint helper functions
  const handleSharePointFileSelected = useCallback(
    async (file) => {
      console.log("=== SHAREPOINT FILE SELECTED ===");
      console.log("File ID:", file.id);
      console.log("File Name:", file.name);
      console.log("MIME Type:", file.mimeType);
      console.log(
        "File Size:",
        file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Unknown"
      );
      console.log("Web URL:", file.webUrl);
      console.log("Full File Object:", file);
      console.log("=== END FILE DETAILS ===");

      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const token = user?.access_token;
      const companyId = user?.company_id;

      if (!companyId || !token) {
        showErrorToast("Missing authentication details");
        return;
      }

      if (!sharePointAccessToken) {
        showErrorToast("SharePoint access token not available");
        return;
      }

      // Prepare the file data to send
      const fileData = {
        ...file,
        access_token: sharePointAccessToken,
      };

      console.log("=== SHAREPOINT FILE UPLOAD ===");
      console.log("File data being sent:", JSON.stringify(fileData, null, 2));
      console.log(
        "SharePoint Access Token (first 20 chars):",
        sharePointAccessToken.substring(0, 20) + "..."
      );
      console.log(
        "API Endpoint:",
        ENDPOINTS.UPLOAD_DOCUMENT_SHAREPOINT(companyId)
      );

      setIsUploadingGoogleDrive(true); // Reusing the same loading state

      try {
        const response = await fetch(
          ENDPOINTS.UPLOAD_DOCUMENT_SHAREPOINT(companyId),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(fileData),
          }
        );

        console.log("Response status:", response.status);
        const data = await response.json();
        console.log("Response data:", data);

        if (!response.ok) {
          showErrorToast(
            data.detail || data.message || "Failed to upload file"
          );
          return;
        }

        showSuccessToast(`File "${file.name}" uploaded successfully!`);
        console.log("Upload response:", data);

        // Navigate to documents page
        navigate("/doc");
      } catch (error) {
        console.error("Error uploading SharePoint file:", error);
        showErrorToast("Failed to upload file. Please try again.");
      } finally {
        setIsUploadingGoogleDrive(false);
      }
    },
    [sharePointAccessToken, navigate]
  );

  const showSharePointFilePicker = useCallback(
    (files, siteName) => {
      // Create a simple modal for file selection
      const modal = document.createElement("div");
      modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    `;

      const modalContent = document.createElement("div");
      modalContent.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 20px;
      max-width: 500px;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

      modalContent.innerHTML = `
      <h3 style="margin: 0 0 15px 0; color: #333;">Select a file from SharePoint (${siteName})</h3>
      <div id="file-list" style="max-height: 300px; overflow-y: auto;">
        ${files
          .map(
            (file) => `
          <div style="padding: 10px; border: 1px solid #ddd; margin: 5px 0; cursor: pointer; border-radius: 4px; hover:background: #f5f5f5;" 
               onclick="window.selectSharePointFile('${file.id}', '${file.name
              }', '${file.file?.mimeType || ""}', '${file.size || 0}', '${file.webUrl || ""
              }')">
            <strong>${file.name}</strong><br>
            <small style="color: #666;">${file.file?.mimeType || "Unknown type"
              } • ${file.size
                ? (file.size / 1024 / 1024).toFixed(2) + " MB"
                : "Unknown size"
              }</small>
          </div>
        `
          )
          .join("")}
      </div>
      <div style="margin-top: 15px; text-align: right;">
        <button onclick="window.closeSharePointModal()" style="padding: 8px 16px; margin-left: 10px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer;">Cancel</button>
      </div>
    `;

      modal.appendChild(modalContent);
      document.body.appendChild(modal);

      // Add global functions for the modal
      window.selectSharePointFile = (
        fileId,
        fileName,
        mimeType,
        fileSize,
        webUrl
      ) => {
        handleSharePointFileSelected({
          id: fileId,
          name: fileName,
          mimeType: mimeType,
          size: parseInt(fileSize),
          webUrl: webUrl,
        });
        window.closeSharePointModal();
      };

      window.closeSharePointModal = () => {
        document.body.removeChild(modal);
        delete window.selectSharePointFile;
        delete window.closeSharePointModal;
      };
    },
    [handleSharePointFileSelected]
  );

  const openSharePointPicker = useCallback(
    async (token) => {
      if (!token) {
        showErrorToast(
          "No SharePoint access token available. Please sign in again."
        );
        return;
      }

      try {
        console.log("Fetching SharePoint sites and files...");

        // First, get the user's SharePoint sites
        const sitesResponse = await fetch(
          `${SHAREPOINT_CONFIG.GRAPH_API_BASE}/sites?search=*`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!sitesResponse.ok) {
          throw new Error(`HTTP error! status: ${sitesResponse.status}`);
        }

        const sitesData = await sitesResponse.json();
        const sites = sitesData.value || [];

        if (sites.length === 0) {
          showErrorToast("No SharePoint sites found.");
          return;
        }

        // Get files from the first site's default document library
        const firstSite = sites[0];
        const filesResponse = await fetch(
          `${SHAREPOINT_CONFIG.GRAPH_API_BASE}/sites/${firstSite.id}/drive/root/children?$filter=file ne null`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!filesResponse.ok) {
          throw new Error(`HTTP error! status: ${filesResponse.status}`);
        }

        const filesData = await filesResponse.json();
        const files = filesData.value || [];

        // Filter files by allowed types
        const allowedMimeTypes = [
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/pdf",
            "image/jpeg",
            "image/png",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "text/plain",
          "application/vnd.oasis.opendocument.text",
        ];

        const filteredFiles = files.filter((file) =>
          allowedMimeTypes.includes(file.file?.mimeType || "")
        );

        if (filteredFiles.length === 0) {
          showErrorToast(
            "No supported files found in SharePoint. Please upload DOC, DOCX, PDF, or XLSX files."
          );
          return;
        }

        // Create a simple file picker modal
        showSharePointFilePicker(filteredFiles, firstSite.displayName);
      } catch (error) {
        console.error("Error fetching SharePoint files:", error);
        showErrorToast("Failed to fetch SharePoint files. Please try again.");
      }
    },
    [showSharePointFilePicker]
  );

  // Handle SharePoint OAuth redirect response
  useEffect(() => {
    const handleSharePointOAuthRedirect = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");

      if (state === "sharepoint_auth") {
        if (error) {
          showErrorToast(`SharePoint OAuth error: ${error}`);
        } else if (code) {
          try {
            // Exchange authorization code for access token
            const tokenResponse = await fetch(SHAREPOINT_CONFIG.TOKEN_URI, {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({
                client_id: SHAREPOINT_CONFIG.APPLICATION_ID,
                client_secret: SHAREPOINT_CONFIG.CLIENT_SECRET,
                code: code,
                grant_type: "authorization_code",
                redirect_uri: SHAREPOINT_CONFIG.REDIRECT_URI,
                scope: SHAREPOINT_CONFIG.SCOPES,
              }),
            });

            const tokenData = await tokenResponse.json();

            if (tokenData.access_token) {
              // Store token with expiry
              const expiry = new Date().getTime() + tokenData.expires_in * 1000;
              localStorage.setItem(
                "sharepoint_access_token",
                tokenData.access_token
              );
              localStorage.setItem(
                "sharepoint_token_expiry",
                expiry.toString()
              );

              setSharePointAccessToken(tokenData.access_token);
              setIsSharePointSignedIn(true);

              // Clear the URL parameters
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );

              // Open SharePoint picker after a short delay
              setTimeout(() => {
                openSharePointPicker(tokenData.access_token);
              }, 1000);
            } else {
              showErrorToast("Failed to get SharePoint access token");
            }
          } catch (error) {
            console.error("Error exchanging SharePoint code for token:", error);
            showErrorToast("Failed to authenticate with SharePoint");
          }
        }

        // Clear the redirect flag
        localStorage.removeItem("sharepoint_auth_redirect");
      }
    };

    // Check if we're returning from SharePoint OAuth redirect
    if (localStorage.getItem("sharepoint_auth_redirect")) {
      handleSharePointOAuthRedirect();
    }
  }, [openSharePointPicker]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      const now = new Date();
      return now.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    try {
      const date = new Date(timestamp);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const messageDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      // Check if the date is today
      if (messageDate.getTime() === today.getTime()) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      } else {
        // Show full date and time for other dates
        return date.toLocaleString([], {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch (error) {
      const now = new Date();
      return now.toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const getUserInitials = (username) => {
    if (!username) return "U";
    return username
      .split(" ")
      .map((name) => name.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const UserAvatar = () => {
    const hasImage = userInfo.image;

    if (hasImage) {
      return (
        <img
          src={userInfo.image}
          alt={userInfo.username || "User"}
          className="w-8 h-8 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
        />
      );
    } else {
      return (
        <div className="w-8 h-8 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center text-white text-sm font-semibold border-2 border-gray-300 dark:border-gray-600">
          {getUserInitials(userInfo.username)}
        </div>
      );
    }
  };

  const RobotAvatar = () => (
    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold border-2 border-gray-300 dark:border-gray-600">
      🤖
    </div>
  );

  const renderMarkdownMessage = (message) => {
    if (typeof message !== "string") return message;
    return <MarkdownMessage text={message} />;
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (hasStartedConversation) {
        handleContinueConversation();
      } else {
        handleStartConversation();
      }
    }
  };

  const handleStartConversation = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const userMessage = {
      id: `u-${Date.now()}`,
      text: trimmed,
      timestamp: formatTimestamp(null),
      rawTimestamp: new Date().toISOString(),
      isUser: true,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details ❌");
      return;
    }

    setLoading(true);

    try {
      // Get chatbot_id from localStorage if available
      const activeChatbotId = localStorage.getItem("active_chatbot_id");

      // Use chatbot-specific endpoint if chatbot_id is available, otherwise use regular endpoint
      const endpoint = activeChatbotId
        ? ENDPOINTS.START_CHATBOT_CONVERSATION(
          companyId,
          userId,
          activeChatbotId
        )
        : ENDPOINTS.START_CONVERSATION(companyId, userId);

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question: trimmed,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Failed to get response ❌");
        return;
      }

      if (!conversationId && (data.conversation_id || data.id)) {
        const newConversationId = data.conversation_id || data.id;
        localStorage.setItem("conversation_id", newConversationId);
        setConversationId(newConversationId);
        setHasStartedConversation(true);

        // Refresh history conversations
        if (window.refreshSidebarConversations) {
          window.refreshSidebarConversations();
        }

        // Refresh chatbot conversations if a chatbot is active
        const activeChatbotId = localStorage.getItem("active_chatbot_id");
        if (activeChatbotId && window.refreshChatbotConversations) {
          window.refreshChatbotConversations(activeChatbotId);
        }

        // Highlight the new conversation in sidebar
        if (window.updateSidebarActiveConversation) {
          window.updateSidebarActiveConversation(newConversationId);
        }
      }

      const aiMessage = {
        id: `a-${Date.now()}`,
        text: data.answer || data.response || data.reply || "",
        timestamp: formatTimestamp(data.created_at),
        rawTimestamp:
          data.created_at || data.answer_created_at || new Date().toISOString(),
        documentSource: normalizeDocumentSource(
          data.document_source ?? data.document_sources
        ),
        isUser: false,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      console.error("Conversation error:", err);
      showErrorToast("Failed to get response");
    } finally {
      setLoading(false);
    }
  };

  const handleContinueConversation = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const conversationId = localStorage.getItem("conversation_id");

    if (!conversationId) {
      showErrorToast("No active conversation found");
      return;
    }

    const userMessage = {
      id: `u-${Date.now()}`,
      text: trimmed,
      timestamp: formatTimestamp(null),
      rawTimestamp: new Date().toISOString(),
      isUser: true,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        ENDPOINTS.CONTINUE_CONVERSATION(companyId, conversationId, userId),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            content: trimmed,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(data.detail || "Failed to get response");
        return;
      }

      const aiMessage = {
        id: `a-${Date.now()}`,
        text: data.answer || data.response || data.reply || "",
        timestamp: formatTimestamp(data.created_at),
        rawTimestamp:
          data.created_at || data.answer_created_at || new Date().toISOString(),
        documentSource: normalizeDocumentSource(
          data.document_source ?? data.document_sources
        ),
        isUser: false,
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err) {
      console.error("Conversation error:", err);
      showErrorToast("Failed to get response");
    } finally {
      setLoading(false);
    }
  };

  const openUploadModal = () => {
    setIsUploadModalOpen(true);
    setIsUploadDropdownOpen(false);
  };

  const toggleUploadDropdown = () => {
    setIsUploadDropdownOpen(!isUploadDropdownOpen);
  };

  // --- Company drive-connection status + disconnect (admin only) -------------
  // Checks whether a company-scoped connection RECORD exists on the backend
  // (200 = exists, 404 = none), regardless of whether this browser holds a valid
  // token. Gating the Disconnect button on this (not on isSignedIn) is what lets
  // an admin clear a stale/expired/orphaned record — the exact state that
  // otherwise blocks every new connect with "already connected for this company".
  const refreshDriveConnectionStatus = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) return;
    const recordExists = async (url) => {
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { "Content-Type": "application/json", "X-User-Id": userId },
        });
        return res.ok; // 200 => a record exists; 404 => none
      } catch (e) {
        return false;
      }
    };
    const [gd, od] = await Promise.all([
      recordExists(ENDPOINTS.SAVE_GOOGLE_DRIVE_TOKEN(companyId)),
      recordExists(ENDPOINTS.SAVE_ONEDRIVE_TOKEN(companyId)),
    ]);
    setGoogleDriveCompanyConnected(gd);
    setOneDriveCompanyConnected(od);
    // SharePoint is its own company connection (separate sign-in with Sites.Read.All consent).
    try {
      const r = await fetch(ENDPOINTS.LIST_DRIVE_TOKENS(companyId), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      if (r.ok) {
        const list = await r.json();
        setSharePointCompanyConnected(
          Array.isArray(list) &&
            list.some((t) => t.drive_type === "sharepoint" && t.is_active)
        );
      }
    } catch (e) {
      /* leave as not connected */
    }
  }, []);

  useEffect(() => {
    // Only admins can see/use the connectors, so only they need this check.
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user?.role === "admin") {
      refreshDriveConnectionStatus();
    }
  }, [refreshDriveConnectionStatus]);

  const handleGoogleDriveDisconnect = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    if (
      !window.confirm(
        "Disconnect Google Drive for your whole company? Anyone using it will need to reconnect. Files already imported are not deleted."
      )
    ) {
      return;
    }
    setIsDisconnectingDrive(true);
    try {
      const res = await fetch(
        ENDPOINTS.DISCONNECT_GOOGLE_DRIVE_TOKEN(companyId),
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "X-User-Id": userId },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || "Failed to disconnect Google Drive"
        );
        return;
      }
      // Reset local Google Drive sign-in state so the UI shows "not connected".
      setIsSignedIn(false);
      setAccessToken(null);
      localStorage.removeItem("google_drive_access_token");
      localStorage.removeItem("google_drive_token_expiry");
      localStorage.removeItem("google_drive_refresh_token");
      setGoogleDriveCompanyConnected(false);
      setIsUploadDropdownOpen(false);
      showSuccessToast(
        "Google Drive disconnected. You can connect a different account now."
      );
    } catch (err) {
      console.error("Error disconnecting Google Drive:", err);
      showErrorToast("Failed to disconnect Google Drive");
    } finally {
      setIsDisconnectingDrive(false);
    }
  }, []);

  // SharePoint Connect – backend OAuth (company-wide, admin only). Uses the same Microsoft
  // app + callback as OneDrive, but asks for Sites.Read.All / Files.Read.All.
  const handleSharePointCompanyConnect = useCallback(async () => {
    setIsUploadDropdownOpen(false);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    try {
      const response = await fetch(ENDPOINTS.SHAREPOINT_OAUTH_LOGIN(companyId), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.detail || data.message || "Failed to get SharePoint OAuth URL");
        return;
      }
      if (!data.auth_url || typeof data.auth_url !== "string") {
        showErrorToast("Invalid OAuth URL from server");
        return;
      }
      window.location.href = data.auth_url;
    } catch (err) {
      console.error("SharePoint OAuth error:", err);
      showErrorToast("Failed to start SharePoint sign-in");
    }
  }, []);

  const handleSharePointCompanyDisconnect = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    if (
      !window.confirm(
        "Disconnect SharePoint for your whole company? Folders synced from SharePoint will stop syncing until you reconnect. Files already imported are not deleted."
      )
    ) {
      return;
    }
    setIsDisconnectingDrive(true);
    try {
      const res = await fetch(ENDPOINTS.DISCONNECT_SHAREPOINT_TOKEN(companyId), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showErrorToast(data.detail || data.message || "Failed to disconnect SharePoint");
        return;
      }
      setSharePointCompanyConnected(false);
      setIsUploadDropdownOpen(false);
      showSuccessToast("SharePoint disconnected. You can connect a different account now.");
    } catch (err) {
      console.error("Error disconnecting SharePoint:", err);
      showErrorToast("Failed to disconnect SharePoint");
    } finally {
      setIsDisconnectingDrive(false);
    }
  }, []);

  const handleOneDriveDisconnect = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    if (
      !window.confirm(
        "Disconnect OneDrive for your whole company? Anyone using it will need to reconnect. Files already imported are not deleted."
      )
    ) {
      return;
    }
    setIsDisconnectingDrive(true);
    try {
      const res = await fetch(ENDPOINTS.DISCONNECT_ONEDRIVE_TOKEN(companyId), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || "Failed to disconnect OneDrive"
        );
        return;
      }
      // Reset local OneDrive sign-in state so the UI shows "not connected".
      setIsOneDriveSignedIn(false);
      setOneDriveAccessToken(null);
      localStorage.removeItem("onedrive_access_token");
      localStorage.removeItem("onedrive_token_expiry");
      localStorage.removeItem("onedrive_refresh_token");
      setOneDriveCompanyConnected(false);
      setIsUploadDropdownOpen(false);
      showSuccessToast(
        "OneDrive disconnected. You can connect a different account now."
      );
    } catch (err) {
      console.error("Error disconnecting OneDrive:", err);
      showErrorToast("Failed to disconnect OneDrive");
    } finally {
      setIsDisconnectingDrive(false);
    }
  }, []);

  // Google Drive Connect – backend OAuth with PKCE from frontend (fixes "Missing code verifier")
  const handleGoogleDriveConnect = useCallback(async () => {
    setIsUploadDropdownOpen(false);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    if (isSignedIn && accessToken) {
      openGoogleDrivePickerFromBackend();
      return;
    }
    try {
      const { codeVerifier, codeChallenge } = await generatePKCEChallenge();
      const loginUrl = new URL(ENDPOINTS.GOOGLE_OAUTH_LOGIN(companyId));
      loginUrl.searchParams.set("code_challenge", codeChallenge);
      loginUrl.searchParams.set("code_challenge_method", "S256");
      const response = await fetch(loginUrl.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.detail || data.message || "Failed to get Google OAuth URL");
        return;
      }
      const authUrl = data.auth_url;
      if (!authUrl || typeof authUrl !== "string") {
        showErrorToast("Invalid OAuth URL from server");
        return;
      }
      localStorage.setItem("google_drive_code_verifier", codeVerifier);
      localStorage.setItem("google_drive_auth_redirect", "true");
      window.location.href = authUrl;
    } catch (err) {
      console.error("Google OAuth error:", err);
      showErrorToast("Failed to start Google sign-in");
    }
  }, [isSignedIn, accessToken, openGoogleDrivePickerFromBackend]);

  const handleGoogleDriveFilePicked = async (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const files = data.docs; // Get all selected files

      // Define allowed file types
      const allowedMimeTypes = [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/pdf",
            "image/jpeg",
            "image/png",
        "application/vnd.ms-excel",

        "text/plain",
        "application/vnd.oasis.opendocument.text",
        "application/vnd.google-apps.document",
      ];

      console.log(`${files.length} file(s) selected from Google Drive`);

      // Filter out unsupported file types
      const validFiles = files.filter((file) => {
        if (!allowedMimeTypes.includes(file.mimeType)) {
          const unsupportedType = file.mimeType || "unknown";
          showErrorToast(
            `File "${file.name}" type "${unsupportedType}" is not supported and will be skipped.`
          );
          console.warn("Unsupported file type:", unsupportedType, file);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        showErrorToast("No valid files selected.");
        return;
      }

      console.log("Valid files to upload:", validFiles);

      // Show initial success message
      if (validFiles.length === 1) {
        showSuccessToast(`File selected: ${validFiles[0].name}`);
      } else {
        showSuccessToast(`${validFiles.length} files selected`);
      }

      // Upload all valid files
      await uploadMultipleGoogleDriveFiles(validFiles);
    } else if (data.action === window.google.picker.Action.CANCEL) {
      console.log("User cancelled file selection");
    }
  };

  // handleGoogleDriveFolderPicked, removeAutomatedFolder, handleGoogleDriveFolderPicker (commented out)

  // OneDrive Connect + Folder Picker flow commented out
  // handleOneDriveFolderSelected, handleOneDriveFolderPicker, removeAutomatedOneDriveFolder

  const uploadGoogleDriveFile = async (file) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.user_id || user?.id;

    if (!companyId || !token) {
      showErrorToast("Missing authentication details");
      return false;
    }

    // Get valid Google Drive access token (refresh if needed)
    const googleDriveAccessToken = await getValidGoogleDriveToken();
    if (!googleDriveAccessToken) {
      showErrorToast(
        "Google Drive access token not available. Please sign in again."
      );
      return false;
    }

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-User-Id": userId,
      };

      // Include user ID and Google Drive access token in request body
      const requestBody = {
        ...file,
        user_id: userId,
        access_token: googleDriveAccessToken,
      };

      // Check if chatbot_id is present in localStorage
      const activeChatbotId = localStorage.getItem("active_chatbot_id");

      // Use chatbot-specific endpoint if chatbot_id is available, otherwise use regular endpoint
      const endpoint = activeChatbotId
        ? ENDPOINTS.UPLOAD_DOCUMENT_GOOGLE_DRIVE_CHATBOT(
          companyId,
          activeChatbotId
        )
        : ENDPOINTS.UPLOAD_DOCUMENT_GOOGLE_DRIVE(companyId);

      console.log("=== GOOGLE DRIVE FILE UPLOAD ===");
      console.log(
        "File data being sent:",
        JSON.stringify(
          { ...requestBody, access_token: "***REDACTED***" },
          null,
          2
        )
      );
      console.log(
        "Google Drive Access Token (first 20 chars):",
        googleDriveAccessToken.substring(0, 20) + "..."
      );
      console.log("API Endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      // Treat non-empty `error` from API as failure even if HTTP status is 200
      const rawError = data && Object.prototype.hasOwnProperty.call(data, "error") ? data.error : null;
      const normalizedError =
        typeof rawError === "string" ? rawError.trim() : rawError || "";
      const hasApiError = !!normalizedError;

      if (!response.ok || hasApiError) {
        const message =
          (data && (data.message || data.detail)) ||
          normalizedError ||
          `Failed to upload "${file.name}"`;

        showErrorToast(message);
        return false;
      }

      const successMessage =
        (data && (data.message || data.detail)) ||
        `File "${file.name}" uploaded successfully`;

      showSuccessToast(successMessage);

      console.log(`Upload response for ${file.name}:`, data);
      return true;
    } catch (error) {
      console.error(`Error uploading Google Drive file ${file.name}:`, error);
      showErrorToast(`Failed to upload "${file.name}". Please try again.`);
      return false;
    }
  };

  const uploadMultipleGoogleDriveFiles = async (files) => {
    setIsUploadingGoogleDrive(true);

    try {
      let successCount = 0;
      let failCount = 0;

      // Upload files one by one
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`Uploading file ${i + 1} of ${files.length}: ${file.name}`);

        const success = await uploadGoogleDriveFile(file);

        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      }

      // Show final summary
      if (files.length === 1) {
        if (successCount === 1) {
          showSuccessToast(`File uploaded successfully!`);
          navigate("/doc");
        }
      } else {
        if (successCount > 0 && failCount === 0) {
          showSuccessToast(`All ${successCount} files uploaded successfully!`);
          navigate("/doc");
        } else if (successCount > 0 && failCount > 0) {
          showSuccessToast(
            `${successCount} file(s) uploaded successfully, ${failCount} failed.`
          );
          navigate("/doc");
        } else {
          showErrorToast(`All ${failCount} files failed to upload`);
        }
      }
    } catch (error) {
      console.error("Error in batch upload:", error);
      showErrorToast("An error occurred during upload");
    } finally {
      setIsUploadingGoogleDrive(false);
    }
  };

  // Helper function to generate PKCE challenge
  const generatePKCEChallenge = async () => {
    // Generate random code verifier
    const randomBytes = new Uint8Array(32);
    crypto.getRandomValues(randomBytes);
    const codeVerifier = btoa(String.fromCharCode(...randomBytes))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    // Create SHA-256 hash of the verifier
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const codeChallenge = btoa(String.fromCharCode(...hashArray))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    return { codeVerifier, codeChallenge };
  };

  // OneDrive Connect – backend OAuth (same as Drive Flow Test: GET auth URL, redirect)
  const handleOneDriveConnect = useCallback(async () => {
    setIsUploadDropdownOpen(false);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    if (!companyId || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }
    if (isOneDriveSignedIn && oneDriveAccessToken) {
      setIsOneDriveFolderMode(false);
      setIsOneDrivePickerOpen(true);
      return;
    }
    try {
      const response = await fetch(ENDPOINTS.ONEDRIVE_OAUTH_LOGIN(companyId), {
        method: "GET",
        headers: { "Content-Type": "application/json", "X-User-Id": userId },
      });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.detail || data.message || "Failed to get OneDrive OAuth URL");
        return;
      }
      const authUrl = data.auth_url;
      if (!authUrl || typeof authUrl !== "string") {
        showErrorToast("Invalid OAuth URL from server");
        return;
      }
      window.location.href = authUrl;
    } catch (err) {
      console.error("OneDrive OAuth error:", err);
      showErrorToast("Failed to start OneDrive sign-in");
    }
  }, [isOneDriveSignedIn, oneDriveAccessToken]);

  const handleOneDriveFileSelect = async (file) => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.user_id || user?.id;

    if (!companyId || !token) {
      showErrorToast("Missing authentication details");
      return;
    }

    // Get valid OneDrive token (refresh if needed)
    const validOneDriveToken = await getValidOneDriveToken();
    if (!validOneDriveToken) {
      showErrorToast("OneDrive access token not available");
      return;
    }

    // If downloadUrl is missing, use the /content endpoint URL directly
    // Backend will handle the redirect
    if (!file["@microsoft.graph.downloadUrl"]) {
      console.log("⚠️ Download URL missing, using /content endpoint URL");
      file["@microsoft.graph.downloadUrl"] = getOneDriveDownloadUrl(file.id);
      console.log(
        "✅ Using /content endpoint URL (backend will handle redirect)"
      );
    }

    // Prepare the file data to send - send the complete file object
    // Same structure as automated ingestion for backend consistency
    // Includes: @odata.context, @odata.etag, id, name, webUrl, size, file object, @microsoft.graph.downloadUrl
    const fileData = {
      ...file,
      user_id: userId,
      company_id: companyId,
      access_token: validOneDriveToken,
    };

    console.log("=== ONEDRIVE FILE UPLOAD (MANUAL) ===");
    console.log("📦 File data structure:", {
      hasODataContext: !!fileData["@odata.context"],
      hasODataEtag: !!fileData["@odata.etag"],
      id: fileData.id,
      name: fileData.name,
      size: fileData.size,
      hasWebUrl: !!fileData.webUrl,
      mimeType: fileData.file?.mimeType,
      hasDownloadUrl: !!fileData["@microsoft.graph.downloadUrl"],
      hasUserId: !!fileData.user_id,
      hasCompanyId: !!fileData.company_id,
      hasAccessToken: !!fileData.access_token,
    });
    console.log(
      "📦 Full File data being sent:",
      JSON.stringify(
        {
          ...fileData,
          access_token: "Hidden for security (present)",
        },
        null,
        2
      )
    );
    console.log(
      "🔑 OneDrive Access Token (first 20 chars):",
      validOneDriveToken.substring(0, 20) + "..."
    );

    // Check if chatbot_id is present in localStorage
    const activeChatbotId = localStorage.getItem("active_chatbot_id");

    // Use chatbot-specific endpoint if chatbot_id is available, otherwise use regular endpoint
    const endpoint = activeChatbotId
      ? ENDPOINTS.UPLOAD_DOCUMENT_ONEDRIVE_CHATBOT(companyId, activeChatbotId)
      : ENDPOINTS.UPLOAD_DOCUMENT_ONEDRIVE(companyId);

    console.log("📡 API Endpoint:", endpoint);

    setIsUploadingGoogleDrive(true); // Reusing the same loading state

    try {
      const headers = {
        "Content-Type": "application/json",
      };
      // Note: user_id is already included in fileData body

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(fileData),
      });

      console.log("Response status:", response.status);
      const data = await response.json();
      console.log("Response data:", data);

      // Treat non-empty `error` from API as failure even if HTTP status is 200
      const rawError =
        data && Object.prototype.hasOwnProperty.call(data, "error")
          ? data.error
          : null;
      const normalizedError =
        typeof rawError === "string" ? rawError.trim() : rawError || "";
      const hasApiError = !!normalizedError;

      if (!response.ok || hasApiError) {
        const message =
          (data && (data.message || data.detail)) ||
          normalizedError ||
          "Failed to upload file";

        showErrorToast(message);
        return;
      }

      const successMessage =
        (data && (data.message || data.detail)) ||
        `File "${file.name}" uploaded successfully!`;

      showSuccessToast(successMessage);
      console.log("Upload response:", data);

      // Close the OneDrive picker modal
      setIsOneDrivePickerOpen(false);

      // Navigate to documents page
      navigate("/doc");
    } catch (error) {
      console.error("Error uploading OneDrive file:", error);
      showErrorToast("Failed to upload file. Please try again.");
    } finally {
      setIsUploadingGoogleDrive(false);
    }
  };

  // Show chat interface only if there are messages AND a conversation_id exists
  // This ensures welcome container shows when clicking chatbot (not conversation)
  const savedConversationId = localStorage.getItem("conversation_id");
  const showChatInterface = messages.length > 0 && !!savedConversationId;

  const TypingIndicator = () => (
    <>
      {loading && (
        <div className="flex justify-start items-end space-x-2">
          <RobotAvatar />
          <div className="bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg px-4 py-2 flex items-center space-x-1">
            <span>Assistant is typing</span>
            <span className="flex space-x-1">
              <span className="dot animate-bounce">.</span>
              <span className="dot animate-bounce200">.</span>
              <span className="dot animate-bounce400">.</span>
            </span>
          </div>
        </div>
      )}
    </>
  );

  // Upload Dropdown Component for Chat Interface (opens upward)
  // Admin-only section shown in the upload menu: the folders THIS chatbot syncs from,
  // with remove + sync-now per folder. Uses the shared company drive account.
  const renderChatbotFolders = () => (
    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
        Folders this chatbot syncs
      </div>
      {chatbotFoldersLoading ? (
        <div className="text-xs text-gray-400 py-1">Loading…</div>
      ) : chatbotFolders.length === 0 ? (
        <div className="text-xs text-gray-400 py-1">
          None yet. Connect a drive below, then add a folder.
        </div>
      ) : (
        chatbotFolders.map((f) => (
          <div key={f.id} className="flex items-center gap-2 py-1">
            <FaFolder className="w-4 h-4 text-amber-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                {f.folder_name || "Folder"}
              </div>
              <div className="text-[10px] text-gray-400">
                {f.drive_type === "onedrive"
                  ? "OneDrive (includes subfolders)"
                  : f.drive_type === "sharepoint"
                  ? "SharePoint (includes subfolders)"
                  : "Google Drive"}
              </div>
            </div>
            <button
              type="button"
              title="Sync now"
              onClick={() => runFolderNow(f.id)}
              disabled={folderActionId === f.id}
              className="text-gray-400 hover:text-indigo-600 disabled:opacity-50"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              title="Remove folder"
              onClick={() => removeChatbotFolder(f.id, f.folder_name)}
              disabled={folderActionId === f.id}
              className="text-gray-400 hover:text-red-600 disabled:opacity-50"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        ))
      )}
    </div>
  );

  const ChatUploadDropdown = () => (
    <div className="relative" ref={chatDropdownRef}>
      <button
        onClick={toggleUploadDropdown}
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 pl-3 dark:hover:text-gray-300"
        type="button"
      >
        <FiPaperclip className="w-6 h-6" />
      </button>

      {isUploadDropdownOpen && (
        <div className="absolute bottom-full right-0 mb-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          <button
            onClick={openUploadModal}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <HiOutlineDocumentAdd className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-medium">Upload Files</span>
          </button>

          {isAdmin && renderChatbotFolders()}

          <button
            onClick={handleGoogleDriveConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={googleDriveIcon} alt="Google Drive" className="w-5 h-5" />
            <span className="text-sm font-medium">
              {isSignedIn ? "Google Drive (Connected)" : "Google Drive"}
            </span>
          </button>
          {isSignedIn && (
            <button
              onClick={openGoogleDriveFolderPickerForIngestion}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
              type="button"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a Google Drive folder</span>
            </button>
          )}
          {isAdmin && googleDriveCompanyConnected && (
            <button
              onClick={handleGoogleDriveDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive
                  ? "Disconnecting…"
                  : "Disconnect Google Drive"}
              </span>
            </button>
          )}

          <button
            onClick={handleOneDriveConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={oneDriveIcon} alt="OneDrive" className="w-5 h-5" />
            <span className="text-sm font-medium">
              {isOneDriveSignedIn ? "OneDrive (Connected)" : "OneDrive"}
            </span>
          </button>
          {isOneDriveSignedIn && (
            <button
              onClick={openOneDriveFolderPickerForIngestion}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
              type="button"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a OneDrive folder</span>
            </button>
          )}
          {isAdmin && oneDriveCompanyConnected && (
            <button
              onClick={handleOneDriveDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive
                  ? "Disconnecting…"
                  : "Disconnect OneDrive"}
              </span>
            </button>
          )}
          {isAdmin && !sharePointCompanyConnected && (
            <button
              onClick={handleSharePointCompanyConnect}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
            >
              <img src={sharePointIcon} alt="SharePoint" className="w-5 h-5" />
              <span className="text-sm font-medium">SharePoint</span>
            </button>
          )}
          {isAdmin && sharePointCompanyConnected && (
            <button
              onClick={openSharePointFolderPickerForIngestion}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a SharePoint folder</span>
            </button>
          )}
          {isAdmin && sharePointCompanyConnected && (
            <button
              onClick={handleSharePointCompanyDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive ? "Disconnecting…" : "Disconnect SharePoint"}
              </span>
            </button>
          )}

          {/* <button
            onClick={handleSharePointConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={sharePointIcon} alt="OneDrive" className="w-5 h-5" />

            <span className="text-sm font-medium">
              {isSharePointSignedIn ? "SharePoint (Connected)" : "SharePoint"}
            </span>
          </button> */}
        </div>
      )}
    </div>
  );

  // Upload Dropdown Component for Welcome Screen (opens downward)
  const WelcomeUploadDropdown = () => (
    <div className="relative" ref={welcomeDropdownRef}>
      <button
        onClick={toggleUploadDropdown}
        className="text-gray-500 hover:text-gray-700 dark:text-text-dark dark:hover:text-gray-200 mr-2"
        type="button"
      >
        <RiFolderUploadLine className="w-10 h-10 text-indigo-900 dark:text-text-dark" />
      </button>

      {isUploadDropdownOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          <button
            onClick={openUploadModal}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <HiOutlineDocumentAdd className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <span className="text-sm font-medium">Upload Files</span>
          </button>

          {isAdmin && renderChatbotFolders()}

          <button
            onClick={handleGoogleDriveConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={googleDriveIcon} alt="Google Drive" className="w-5 h-5" />
            <span className="text-sm font-medium">
              {isSignedIn ? "Google Drive (Connected)" : "Google Drive"}
            </span>
          </button>
          {isSignedIn && (
            <button
              onClick={openGoogleDriveFolderPickerForIngestion}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
              type="button"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a Google Drive folder</span>
            </button>
          )}
          {isAdmin && googleDriveCompanyConnected && (
            <button
              onClick={handleGoogleDriveDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive
                  ? "Disconnecting…"
                  : "Disconnect Google Drive"}
              </span>
            </button>
          )}

          <button
            onClick={handleOneDriveConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={oneDriveIcon} alt="OneDrive" className="w-5 h-5" />
            <span className="text-sm font-medium">
              {isOneDriveSignedIn ? "OneDrive (Connected)" : "OneDrive"}
            </span>
          </button>
          {isOneDriveSignedIn && (
            <button
              onClick={openOneDriveFolderPickerForIngestion}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
              type="button"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a OneDrive folder</span>
            </button>
          )}
          {isAdmin && oneDriveCompanyConnected && (
            <button
              onClick={handleOneDriveDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive
                  ? "Disconnecting…"
                  : "Disconnect OneDrive"}
              </span>
            </button>
          )}
          {isAdmin && !sharePointCompanyConnected && (
            <button
              onClick={handleSharePointCompanyConnect}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
            >
              <img src={sharePointIcon} alt="SharePoint" className="w-5 h-5" />
              <span className="text-sm font-medium">SharePoint</span>
            </button>
          )}
          {isAdmin && sharePointCompanyConnected && (
            <button
              onClick={openSharePointFolderPickerForIngestion}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
            >
              <FaFolder className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-medium">Add a SharePoint folder</span>
            </button>
          )}
          {isAdmin && sharePointCompanyConnected && (
            <button
              onClick={handleSharePointCompanyDisconnect}
              disabled={isDisconnectingDrive}
              type="button"
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-3 text-red-600 dark:text-red-400 transition-colors disabled:opacity-50"
            >
              <FiXCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {isDisconnectingDrive ? "Disconnecting…" : "Disconnect SharePoint"}
              </span>
            </button>
          )}

          {/* <button
            onClick={handleSharePointConnect}
            className="w-full text-left px-4 py-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <img src={sharePointIcon} alt="OneDrive" className="w-5 h-5" />

            <span className="text-sm font-medium">
              {isSharePointSignedIn ? "SharePoint (Connected)" : "SharePoint"}
            </span>
          </button> */}
        </div>
      )}
    </div>
  );

  return (
    <>
      {(isUploadingGoogleDrive || isLoadingConversation) && <Loader />}
      {showChatInterface ? (
        <>
          <div className="min-h-[98%] max-h-[99.9%] dark:bg-dark flex flex-col">
            <div className="flex-1 px-6 md:px-10 py-4 space-y-4 overflow-y-auto no-scrollbar">
              {messages.map((message) => (
                <div
                  key={message.id}
                  ref={(el) => {
                    if (el) {
                      messagesRef.current[message.id] = el;
                    }
                  }}
                  className={`flex space-x-2 ${message.isUser
                    ? "items-center justify-end"
                    : "justify-start items-start"
                    }`}
                >
                  {!message.isUser && <RobotAvatar />}

                  <div className="max-w-4xl">
                    {message.isUser ? (
                      <div className="bg-gray-200 dark:bg-bg-light rounded-lg px-4 py-2 mb-1">
                        <p className="text-black whitespace-pre-wrap">
                          {message.text}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-bg-dark dark:bg-bg-light text-white dark:text-text-dark rounded-lg px-4 py-3 mb-1">
                        <div className="prose dark:prose-invert max-w-none leading-relaxed">
                          {renderMarkdownMessage(message.text)}
                        </div>

                        {(() => {
                          const sources = normalizeDocumentSource(
                            message.documentSource
                          );
                          return sources.length > 0 ? (
                            <div className="mt-3 pt-3 border-t border-gray-500 dark:border-gray-600">
                              <p className="text-xs text-gray-300 dark:text-text-primary mb-1.5 flex items-center gap-1">
                                <FaFileAlt className="text-gray-300 dark:text-black shrink-0" />
                                {sources.length === 1
                                  ? "Source:"
                                  : "Sources:"}
                              </p>
                              <ul className="space-y-1">
                                {sources.map((source, idx) => (
                                  <li
                                    key={idx}
                                    className="text-xs flex text-gray-300 gap-1 dark:text-text-primary items-center"
                                  >
                                    <span className="font-medium text-blue-400 dark:text-text-accent">
                                      {source}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}

                    <p
                      className={`text-xs text-gray-500 dark:text-text-light px-2 ${message.isUser ? "text-right" : "text-left"
                        }`}
                    >
                      {message.rawTimestamp
                        ? (() => {
                          const localDate = moment.utc(message.rawTimestamp).local();
                          if (localDate.isSame(moment(), "day")) {
                            return `Today at ${localDate.format("h:mm A")}`;
                          }
                          return localDate.format("MMM D, YYYY h:mm A");
                        })()
                        : message.timestamp}
                    </p>
                  </div>

                  {message.isUser && <UserAvatar />}
                </div>
              ))}

              <TypingIndicator />

              <div ref={messagesEndRef} />
            </div>

            {/* Input with dropdown opening upward */}
            <div className="bg-gray-200 flex border-t dark:bg-bg-dark dark:border-gray-700 px-6 py-4 sticky bottom-0">
              <div className="flex items-center w-full bg-white rounded-lg px-4">
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 bg-transparent text-lg focus:outline-none text-gray-900 dark:text-text-dark placeholder-gray-500 dark:placeholder-gray-400"
                />
                {isAdmin && <ChatUploadDropdown />}
              </div>

              <button
                onClick={handleContinueConversation}
                disabled={!inputValue.trim() || loading}
                className={`ml-3 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${inputValue.trim() && !loading
                  ? "bg-indigo-900 dark:bg-bg-light dark:text-text-dark text-white hover:bg-indigo-800 dark:hover:bg-indigo-600"
                  : "bg-gray-300 dark:bg-bg-light text-gray-500 dark:text-text-dark cursor-not-allowed"
                  }`}
                aria-label="Send message"
              >
                <BsFillSendFill />
              </button>
            </div>
          </div>

          <UploadModal isOpen={isUploadModalOpen} onClose={closeUploadModal} />
          <OneDriveFilePicker
            isOpen={isOneDrivePickerOpen}
            onClose={() => {
              setIsOneDrivePickerOpen(false);
              setIsOneDriveFolderMode(false);
            }}
            accessToken={oneDriveAccessToken}
            onFileSelect={handleOneDriveFileSelect}
            onFolderSelect={() => { }}
            folderMode={isOneDriveFolderMode}
          />
        </>
      ) : (
        <>
          {/* welcome message container */}
          <div className="relative flex items-center justify-center py-48 bg-bg-light dark:bg-bg-dark px-4 ">
            {loading && (
              <div className="absolute inset-0 bg-bg-light dark:bg-bg-dark bg-opacity-90 dark:bg-opacity-90 z-50 flex items-center justify-center">
                <Loader />
              </div>
            )}
            <div className="text-center w-full mx-auto space-y-10">
              {/* Chatbot Name with Icon */}
              {chatbotName && (
                <div className="flex items-center justify-center gap-3 mb-6">
                  <SiChatbot className="text-4xl md:text-5xl text-indigo-900 dark:text-white" />
                  <h2 className="text-2xl md:text-4xl font-semibold text-indigo-900 dark:text-white">
                    {chatbotName}
                  </h2>
                </div>
              )}

              <div className="flex justify-center">
                <div className="flex items-center bg-white shadow-lg rounded-full px-4 py-3 w-full max-w-6xl border border-gray-200 dark:border-gray-700">
                  {isAdmin && <WelcomeUploadDropdown />}
                  <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 bg-transparent px-3 focus:outline-none text-gray-900 dark:text-text-dark placeholder-gray-500 dark:placeholder-gray-400"
                    autoFocus
                  />
                  <button
                    onClick={handleStartConversation}
                    disabled={!inputValue.trim() || loading}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${inputValue.trim() && !loading
                      ? "bg-bg-dark text-white hover:bg-indigo-800 dark:hover:bg-indigo-600"
                      : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-white cursor-not-allowed"
                      }`}
                    type="button"
                  >
                    <FaArrowUpLong />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <UploadModal isOpen={isUploadModalOpen} onClose={closeUploadModal} />
          <OneDriveFilePicker
            isOpen={isOneDrivePickerOpen}
            onClose={() => {
              setIsOneDrivePickerOpen(false);
              setIsOneDriveFolderMode(false);
            }}
            accessToken={oneDriveAccessToken}
            onFileSelect={handleOneDriveFileSelect}
            onFolderSelect={() => { }}
            folderMode={isOneDriveFolderMode}
          />
        </>
      )}
      <GoogleDrivePicker
        isOpen={googleDrivePickerOpen}
        onClose={() => {
          setGoogleDrivePickerOpen(false);
          setGoogleDrivePickerData(null);
        }}
        data={googleDrivePickerData}
        mode={drivePickerMode}
        title={drivePickerTitle}
        confirmLabel={drivePickerConfirmLabel}
        onSelect={(selected) => {
          if (drivePickerMode === "folders") {
            const folder = selected?.[0];
            if (!folder?.id) return;
            createIngestionFolderMonitor({
              driveType: drivePickerDriveType,
              folderId: folder.id,
              folderName: folder.name || "Folder",
              intervalHours: 5,
            });
          } else {
            if (selected.length === 1)
              showSuccessToast(`File selected: ${selected[0].name}`);
            else if (selected.length > 1)
              showSuccessToast(`${selected.length} files selected`);
            uploadMultipleGoogleDriveFiles(selected);
          }
          setGoogleDrivePickerOpen(false);
          setGoogleDrivePickerData(null);
        }}
      />
      <DriveFolderPicker
        isOpen={driveFolderPickerOpen}
        onClose={() => setDriveFolderPickerOpen(false)}
        driveType={driveFolderPickerType}
        companyId={JSON.parse(localStorage.getItem("user") || "{}")?.company_id}
        userId={
          JSON.parse(localStorage.getItem("user") || "{}")?.id ||
          JSON.parse(localStorage.getItem("user") || "{}")?.user_id
        }
        onSelectFolder={(folder) => {
          if (!folder?.id) return;
          createIngestionFolderMonitor({
            driveType: driveFolderPickerType,
            folderId: folder.id,
            folderName: folder.name || "Folder",
            driveId: folder.driveId,
            intervalHours: 5,
          });
        }}
      />
    </>
  );
}
